// Static help content — FAQ + Knowledge Base articles + Interactive Tour steps.
// Markdown is rendered as plain whitespace-preserving text on /help.

export const FAQ = [
  {
    q: "What's the Strategic Plan and the Hierarchical Overview?",
    a: "Once a plan graduates from Launching → Operating Mode, your Business Plan becomes a Strategic Plan — a live SVG chart of Business Drivers → Strategic Goals → Projects → Roadmap Tasks on one screen. Edges are health-coloured (green healthy, amber drifting, red red-line), a draggable Mini-Map lets you scrub around big plans in both axes, and every node is a click-through into the source surface. Launching plans get the same treatment via the Pre-Launch Overview (Business Plan → Compliance → Health → Goals). Set the default view (Overview vs. classic sections) per-user under Settings > Workspace.",
  },
  {
    q: "How does the Attribution Loop work?",
    a: "Every closed-won CRM deal and every marketing campaign carries an origin_ref back to the Strategic Goal or Roadmap Task that sparked it. That link powers a 4-week revenue sparkline on every Goal card, one-click filters on the CRM and Campaign lists (e.g. /crm?origin_goal=<goal-id>), a task-level attribution chip on Kanban cards, and Prefill From Origin — click 'Add to CRM' or 'Add to Campaigns' from a Goal and the new-record dialog opens prefilled AND pre-stamped with the origin link. Result: you can finally answer 'how much revenue did this Goal actually produce?' without a spreadsheet.",
  },
  {
    q: "I have an idea but no business experience. Will this still work for me?",
    a: "Yes — Three Little Birds was built for exactly this case. The 5-step wizard takes 10-15 minutes and assumes zero prior knowledge. We translate your raw idea into a strategist-grade plan, pricing model, 3-year projection, and a step-by-step execution roadmap. You don't need to know what a SWOT analysis is. We do that part.",
  },
  {
    q: "How accurate are the Three Little Birds-generated plans?",
    a: "The plan is a rigorous starting point — not a finished document. It's grounded in industry templates, market signals, and your specific answers (including the new Personal Investment input on the wizard, which anchors Three Little Birds's runway and infrastructure recommendations). Treat it as a 70% draft you'll refine. Every section can be regenerated independently if a part doesn't match your vision — Infrastructure is capped at one regeneration unless you edit your Description, all other regenerable sections at three.",
  },
  {
    q: "Is my idea private? Can other users see it?",
    a: "Your plans are private to your account. Sharing only happens when YOU explicitly invite a collaborator (Standard or Advanced) or publish a public read-only link (Standard or Advanced). We never train models on your content.",
  },
  {
    q: "What if I want to start a business but I'm scared of failing?",
    a: "That fear is the single biggest reason most ideas die before they begin. Three Little Birds removes the guesswork — you'll see your runway, your compliance gaps, your launch-readiness score, and your weekly focus before you spend a dollar. Fear shrinks when the path is visible.",
  },
  {
    q: "Do I need to pay to get started?",
    a: "Every new account gets a 7-day free Standard trial — full access to compliance, grants, mentor, plan health, and budget tracking. During the trial, Competitive Analysis, Customer Acquisition, Infrastructure, Pricing Model, and 3-Year Revenue Projection are shown as samples (your real Three Little Birds-generated numbers unlock the moment you subscribe). After the trial there are three paid tiers: Basic ($15/mo) for one founder testing the waters (5 plans, goals + watermarked Plan PDF), Standard ($25/mo) for the full launch OS (20 plans, everything except investor scenarios), and Advanced ($35/mo) for serial founders & agencies (unlimited plans, investor-scenario generation, plus Email & SMS campaigns). Annual billing saves one month on every tier.",
  },
  {
    q: "What happens when my trial or subscription expires?",
    a: "If you subscribed and then lapsed: your data is preserved indefinitely. The dashboard shows an 'EXPIRED' banner with a Resubscribe button; business details lock until you re-subscribe. Compliance CSV / ICS exports stay available even after expiry. If you DIDN'T convert from the free trial: your business plans are permanently deleted 21 days after the trial ends. Subscribing any time before that 21-day window unlocks everything instantly with your real Three Little Birds-generated numbers intact.",
  },
  {
    q: "What's the difference between the Founder Mentor and the in-task service agents?",
    a: "The Mentor is a cross-business advisor — ask it 'what should I prioritize across all my plans?' or 'draft an investor update'. Service Agents are task-specific (e.g. Stripe Atlas, ZenBusiness, Mercury) and only know about ONE roadmap item — perfect when you're filing your LLC and need step-by-step guidance for that exact form.",
  },
  {
    q: "How do I find vetted services or vendors for a roadmap task?",
    a: "Open any task on the launch board and, under \"Get-it-done agents\", tap Find services. Three Little Birds suggests 2-4 real providers matched to that exact task (e.g. Stripe Atlas or ZenBusiness for incorporation, Mercury for banking, Gusto for payroll), some marked \"Vetted\". Each suggestion opens an in-app agent that guides you through that vendor for that specific step, plus a Visit site button. Available on Standard and Advanced — tap Regenerate any time for fresh picks.",
  },
  {
    q: "How do grants work?",
    a: "We pull live federal grants from Grants.gov, add Three Little Birds-sourced state, local, and private programs (Hello Alice, Amber Grant, IFundWomen, Comcast RISE…), and run a live web-cited search (Perplexity) that surfaces state, city and private programs with real, clickable source URLs you can verify. Everything is scored against your business, and you can start a tailored application draft directly inside the app. Each match is tagged by source — FEDERAL · grants.gov, Three Little Birds-SUGGESTED, or WEB-CITED.",
  },
  {
    q: "Can the app generate the financial documents a bank or lender requires?",
    a: "Yes — on Standard and Advanced, the Lender-ready financial package turns your plan and budget into a bank-formatted PDF: a 3-statement model (Projected P&L with Year-1 monthly detail, Cash Flow, and a Balance Sheet that balances), Sources & Uses, Break-even, and DSCR per year — plus an Assumptions page and blank borrower forms (Personal Financial Statement, Debt Schedule, Collateral Schedule). You enter the loan amount, term, APR, and equity injection; the statements are formula-driven so they tie out. Find it under PLAN ACTIONS → Financing on any plan. You still supply lender items like tax returns, bank statements, and personal credit.",
  },
  {
    q: "What is the Market research feature?",
    a: "A Standard+ feature embedded in your Business Plan's Market analysis section (look for the \"The market, with receipts\" panel under Three Little Birds market-analysis block). One click runs a live web search and returns TAM/SAM/SOM market sizing, current growth trends, the most relevant competitors, demand signals, and 3-5 recommendations — with every figure backed by a real, clickable source URL (powered by Perplexity Sonar). It takes ~15-25 seconds and uses a small research credit per run, so Regenerate when you want a fresh pull. When a report exists, a Market research slide is automatically added to your Pitch deck (PDF and PowerPoint) so investors see your market sizing with sources.",
  },
  {
    q: "How do I compare my numbers to my industry?",
    a: "Once your Business Plan is complete, open the Industry benchmarks section. Beyond the cohort comparison (vs. other Three Little Birds founders), you'll see curated Industry standard ratios — typical ranges for gross margin, cost of goods, labor, rent, marketing, net margin and growth for your industry. Your own figures (derived from your budget and revenue projection) are shown next to each range, and anything outside the norm on the wrong side is flagged as an assumption to revisit. Each flag has a 'Fix this' deep-link that jumps you straight to the exact budget or revenue line driving it (with a brief highlight pulse). You can also tag each budget line with its benchmark category from a dropdown, and the benchmarks auto-refresh as you retag — so labor, rent and marketing roll up exactly the way you intend. Tap Snapshot PDF to download a one-page \"your business vs. your industry\" sheet — and the same benchmark page is automatically included in your Lender package PDF and Pitch deck export, so investors and bankers see how you stack up. Available on Standard and Advanced.",
  },
  {
    q: "What is the What-if scenario planner?",
    a: "An Advanced feature that lets you model alternate forecasts without touching your real plan. Open PLAN ACTIONS → What-if planner on any plan, then create named scenarios by changing four levers — Year-1 revenue, annual growth %, cost of goods %, and monthly operating spend. Each scenario computes a deterministic 3-year P&L (Year-3 net income, 3-year cumulative profit, break-even revenue, the year you turn profitable) and shows the delta versus your base plan, side-by-side. When you've built a Driver-based forecast, your scenarios automatically layer on top of it instead of the simpler base plan. Great for stress-testing 'what if growth is slower' or 'what if we run leaner' before you commit.",
  },
  {
    q: "What is the Driver-based forecast?",
    a: "An Advanced feature (PLAN ACTIONS → Driver forecast, or /business/{id}/forecast) that builds a bottom-up, 3-year MONTHLY P&L from the real drivers of your business instead of a single top-line guess: revenue streams (units × price × monthly growth), personnel (roles, headcount and start months → payroll), and operating expenses. Hit 'Sync from Budget' to pull your operating expenses straight from your Budget & burn module in one click — and if you later change the Budget, the page flags that it's drifted so you can re-sync. Each expense carries its benchmark category so it rolls up correctly. The result feeds a live chart, a monthly P&L table and KPI tiles, exports as a 36-month CSV, and powers your what-if scenarios, lender package and pitch deck.",
  },
  {
    q: "I don't know where to start — is there an example I can copy?",
    a: "Yes. Open Examples in the top nav (or the 'Browse example plans' button on an empty dashboard) to see polished sample plans across industries — coffee shop, e-commerce, SaaS, tutoring, salon, trades and more. Preview any one to study its strategy, financials and roadmap, then click Use this to clone it into your workspace as a fully editable plan you can tailor to your own venture. Sample plans are available on every tier.",
  },
  {
    q: "Can I customize what's in my pitch deck or lender PDF?",
    a: "Yes. On the lender package export (Plan actions → Financing) there's an 'Include the industry-benchmark page' checkbox so you can add or omit the 'you vs. your industry' page per download. For the pitch deck, open Plan actions → Customize deck to reorder slides (drag, or use the up/down arrows) and toggle any slide on or off — your layout is saved per plan and applied every time you download. To move even faster, load a built-in preset: 'Investor' (story-led order emphasizing traction, market and the ask) or 'Banker' (financials-forward for a lender), then tweak from there. You can also save your own current layout as a named preset and reuse it across plans. The cover slide always comes first, and data-dependent slides (like the live CRM pipeline) only appear when there's data to show.",
  },
  {
    q: "What's the Plan Health Check and how is the score calculated?",
    a: "A 0-100 hybrid score: 50% deterministic (computed from real data — Runway, Budget completeness, Revenue realism & Roadmap balance — each scored out of 25 pts) + 50% LLM qualitative judgment (founder fit, strategy coherence, market timing). The 'What drives the score' panel breaks out all four deterministic components so you see exactly which lever to pull. Customer acquisition was removed Feb 17 · 2026 — founders can't reliably predict which channels will work, and scoring guessed channels was penalising honest uncertainty. Health Check is exempt from the 3-regeneration cap that applies to other sections — re-run it as often as you want as your plan evolves.",
  },
  {
    q: "What are all the Health scores I see across the app?",
    a: "Six 0–100 scores that answer six different questions. **Plan Health** — the hybrid Three Little Birds+data score on your Business Plan (see the question above). **Launch Health** — plain average of your Plan, Roadmap and Compliance progress (33% · 33% · 34%); answers 'am I ready to open the doors?'. **Business Health** — the Hub's north-star verdict blending Launch readiness, Financial Health and Growth Health (33 · 33 · 34); answers 'are the fundamentals solid right now?'. **Operating Health** — only for plans in Operate mode; blends Revenue trend, Financial Health, Marketing and Launch Health (30 · 30 · 20 · 20); answers 'is the live business trending up?'. **Financial Health** — Runway, Capital stack, Net-vs-plan and Marketing (25% each); answers 'is the money picture holding up?'. **Growth Health** — Audience, Reach, Conversion and Pipeline (25% each); answers 'is traction building?'. Every leg on every score is a clickable pill wired to the feature that moves the number, so the score is never a dead-end.",
  },
  {
    q: "What's the Launch-readiness score (on the Dashboard card)?",
    a: "A 0-100 grade (A/B/C/D/F) blending five dimensions: Plan Health (25%), Compliance Risk (20%), Roadmap Completion (20%), Budget Runway (20%), and Goals Progress (15%). Missing dimensions are re-normalized away so the score stays meaningful while you're still filling things in. Click the badge to see exactly which dimensions are pulling your grade down. On the Hub, a simpler headline number — 'Launch Health' — is the plain average of Plan, Roadmap and Compliance progress; the two agree on the big picture and disagree only on advisory weight.",
  },
  {
    q: "Can I export my plan as a PDF?",
    a: "Yes — Plan PDF is available on every paid tier (watermarked on Basic, clean on Standard and Advanced). Pitch Deck (10-slide landscape investor deck) is Standard and Advanced only. The PDFs honor your Brand Kit logo, tagline, and colors set in Settings.",
  },
  {
    q: "Can I change Three Little Birds's writing voice?",
    a: "Yes — go to Settings. Pick from 5 voice presets (Default, Formal, Friendly, Concise, Bold) plus a 500-character custom-instruction box. Every plan, focus list, mentor reply, and grant draft uses your voice from then on.",
  },
  {
    q: "What if I want to start over with a different business?",
    a: "Each plan is independent. Create as many as you want from the Dashboard — each gets its own roadmap, budget, compliance, goals, and weekly focus.",
  },
  {
    q: "What is the 'How much are you investing?' question for?",
    a: "It's the founder's personal capital going into the business — savings, personal loans, family contributions. Three Little Birds uses it to size your runway and to tailor the Infrastructure section (a $5K self-funded plan gets leaner staffing and tooling recommendations than a $250K backed one). Leave it as 0 if you're entirely pre-funding-decision — the field is optional, and you can edit it later via the wizard.",
  },
  {
    q: "Why does Infrastructure only let me regenerate it once?",
    a: "The three Infrastructure columns (Personnel · Technology · Administration) regenerate as a single coherent unit and feed straight into your Budget — so we cap manual regeneration at one to avoid the budget drifting out of sync with rerolled staffing lists. If you need another pass, open the wizard and edit the Description: changing the description automatically resets the Infrastructure regen counter, since the new framing is what should drive a fresh staffing/tools list.",
  },
  {
    q: "I've already completed my Personality Profile. Why do I sometimes get sent there again?",
    a: "You shouldn't anymore. Logging in (email/password or Google) routes you straight to the Dashboard the moment your Personality Profile has been saved or skipped. If you're still being asked to fill it out, your saved profile lost its updated_at flag — re-save once from the Profile page and the next login will land directly on /dashboard.",
  },
  {
    q: "What happens if I get stuck?",
    a: "Click the floating Mentor button (bottom-right) any time and ask. It has full context on every business you've created — it can tell you exactly what's blocking you and how to unblock it.",
  },
  {
    q: "Is there a CRM to track sales leads and deals?",
    a: "Yes — on Standard and Advanced, every plan has a native CRM. Track contacts, companies, and a drag-and-drop deal pipeline (Lead → Qualified → Proposal → Negotiation → Won/Lost). Closed-won deals automatically feed your Goals progress and extend your Runway, and a Pipeline summary lives right inside the Business Plan (after Funding strategy). The workspace-wide CRM Dashboard at /crm rolls every plan's pipeline into one view with charts, a win-rate tile, date filters, and CSV export.",
  },
  {
    q: "Where do I store contracts, licenses, and other documents?",
    a: "The Document Library (Standard+) is a per-plan and workspace-wide vault for your files — contracts, invoices, IDs, licenses, logos. Upload to folders, add tags, download securely, and click any tag to filter. Three Little Birds 'Analyze' reads images and PDFs to auto-summarize, classify, and tag them. Open it per-plan from the plan's Plan-actions menu, or browse everything at /documents with grid or table views.",
  },
  {
    q: "Can I connect QuickBooks Online or Xero to track my actual spend?",
    a: "Yes — on Standard and Advanced you can connect QuickBooks Online or Xero in one click from Settings → Accounting integrations (/settings/integrations). We pull your last 30 days of transactions on demand — strictly READ-ONLY, we never write anything back to your books — then let you 'Apply to Budget Actuals' to merge revenue and expenses into the right month under a single 'Imported (QBO/XERO)' line. Re-applying is safe: it overwrites, never doubles. Disconnect any time and your tokens are deleted. If one-click connect isn't available yet for your account, a step-by-step CSV import guide (/help/qbo-xero-import) gets you the exact same result in about three minutes.",
  },
  {
    q: "Can I send Email or SMS campaigns to my contacts?",
    a: "Yes — Marketing Journeys (an Advanced feature) let you run outreach right from a business plan or the workspace-wide Marketing dashboard. Open a plan's Marketing tab (Plan-actions → Marketing, /business/{id}/campaigns) or Tools → Marketing (/marketing) for a cross-plan rollup. For one-off blasts, build reusable Email & SMS templates. For multi-touch nurture, open the Journey canvas (Plan-actions → Journeys, /business/{id}/journeys) and chain 5 node types — Email · SMS · Wait · Split · End — into a live automation triggered by manual enrolment, new-contact creation, entry into any of the 8 built-in Smart Segments, or entry into a saved Contact Group. Personalise every message with a rich set of placeholders — {{first_name}}, {{name}}, {{email}}, {{phone}}, {{company}} and {{business_name}} — surfaced right below every subject and body input as a click-to-insert chip strip so you never have to memorise the tokens. A live Cost Guard forecasts each journey's projected month-to-date sends against your global monthly Send Budget AND your per-segment sub-caps (Settings → Segment budgets) — anything that would exceed either cap is blocked before it can drain your quota. Each campaign card tracks Sent, Delivered, Opened and Clicked; email opens are tracked via pixel and clicks via a tracked redirect. Email is delivered live via our email provider. SMS defaults to sandbox mode; connect an SMS provider in your backend .env to flip real sends on — delivery status callbacks then update your Delivered / Failed counters automatically.",
  },
  {
    q: "Can Three Little Birds draft social media ads for Meta / Google / TikTok / LinkedIn?",
    a: "Yes — Ad Copy Studio (Advanced) generates 3 platform-tailored social media ad variants per platform in one call: Meta headlines + primary text + CTAs, Google's 3 headlines + 2 descriptions, TikTok hooks + hashtags, and LinkedIn intros + CTAs — all with per-platform character-limit guards so nothing overflows. Pick one of your active Products/Services, a target audience (segment or free-text), and a tone (Bold · Friendly · Expert · Playful). Every draft has a Copy CSV export that auto-appends UTM tags (utm_campaign=<draft_id>, utm_content=v<idx>) to your landing URL so every click routes into the CRM's attribution loop. When a deal flips to Won, Three Little Birds also fires a server-side Meta Conversions API (Layer C) Purchase event with SHA-256-hashed PII so your Meta bidding optimises for real closed customers, not just form fills. Regenerate any variant and thumbs-up/down each one — the studio biases future generations toward what works for you. Find it at Plan-actions → Marketing tools → Ad Copy Studio, or /business/{id}/ad-copy.",
  },
  {
    q: "Can I trace a closed deal back to the exact ad that produced it?",
    a: "Yes — Ads Tracking + CRM Attribution (Advanced) closes the loop from ad → deal → revenue. Paste your Meta Pixel ID and Google Ads Conversion ID once and Three Little Birds regenerates a copy-paste <head> snippet you drop into your landing page. Every closed-won deal can be attributed to a specific Ad Copy Studio draft × variant in three ways: automatic UTM parsing when you paste the inbound click URL, a manual draft × variant picker in the deal drawer, or a multi-select 'Bulk attribute' action on the CRM Pipeline that stamps N deals in one write. Same multi-select carries a 'Clear attribution' action for mis-tagged batches, both actions come with a 24-hour Undo toast (72h on Advanced tier by default, admin-tunable), and a server-side Meta Conversions API push fires a Purchase event when a deal flips to Won so your bidding optimises for real revenue. Above the Pipeline tiles sits a Filter Pills strip (Revenue / Win rate / Recent sort) that deep-jumps into a pipeline filtered to just that ad's deals. A 0–100 Attribution Health Score chip + 12-week sparkline trend shows whether your creative is improving week-over-week.",
  },
  {
    q: "Is there a Reports dashboard with charts I can slice and print?",
    a: "Yes — on Standard and Advanced, every plan has an interactive Reports dashboard (Plan actions → Reports, or /business/{id}/reports). A collapsible left sidebar switches between twelve visual reports: Budget, Pipeline, Funding, Cash flow, Unit economics, Break-even, Revenue bridge, Market size, Scenarios, Timeline, Sensitivity, and a one-page Investor snapshot. Each report opens with a short plain-English note explaining what it shows and how to read it (dismiss it once you know your way around). The date / product-service / customer-segment / stage filters drive the Pipeline report and only appear while Pipeline is selected. The Investor snapshot has a Print / save PDF button that prints just that report as a clean one-page packet for a banker or investor.",
  },

  // ---- Long-tail SEO Q&As (Feb 2026) — pricing, comparisons, use cases, industries, ----
  // ---- integrations, privacy, exports, mobile, onboarding, support, funding, health   ----

  {
    q: "How does Three Little Birds compare to LivePlan?",
    a: "LivePlan is a spreadsheet-first plan writer with templates and forecasting. Three Little Birds goes further in three directions: a Three Little Birds that drafts every plan section from a 5-step wizard (LivePlan makes you type each section), a hybrid Plan Health score plus five other Health scores that turn the plan into a live dashboard, and a full execution layer — launch board with critical path, CRM + deal pipeline, jurisdiction-aware compliance tracker, grants finder, and an investor marketplace — that LivePlan doesn't ship. Pricing starts at $15/month vs LivePlan's ~$20/month, with a 7-day free preview of the Standard tier.",
  },
  {
    q: "How does Three Little Birds compare to Bizplan?",
    a: "Bizplan lets you assemble a plan block-by-block with a step-based UI. Three Little Birds drafts the plan for you in under 60 seconds from a 5-step wizard, then keeps your plan alive — a 0-100 Plan Health score, an executable roadmap with a critical path, budget-vs-actual tracking, and bank-ready lender packages. Bizplan is stronger if you want manual control over each block; Three Little Birds is stronger if you want a Three Little Birds-drafted plan you can iterate + actually operate against.",
  },
  {
    q: "How does Three Little Birds compare to Enloop?",
    a: "Enloop grades your plan on a written-text quality score and lets you export a PDF. Three Little Birds grades your plan on a hybrid 0-100 score that blends real deterministic data (runway, budget completeness, revenue realism, roadmap balance) with a qualitative LLM judgment — so your score improves as you actually build the business, not just as you polish the copy. Add the execution layer (roadmap, CRM, compliance, grants, campaigns) and it's a full launch operating system, not just a plan formatter.",
  },
  {
    q: "How is this different from asking ChatGPT to write a business plan?",
    a: "ChatGPT will draft you plausible prose, but it doesn't do the math, doesn't stay in sync with your budget, doesn't produce a lender-ready 3-statement financial package, doesn't track your jurisdiction's licenses, doesn't find grants matched to your business, doesn't monitor your Plan Health as you edit, and doesn't let investors browse a curated marketplace of ready plans. Three Little Birds uses Three Little Birds where Three Little Birds is strong (drafting narrative sections, spotting risks) and deterministic code where determinism matters (runway, forecasts, tax, compliance).",
  },
  {
    q: "Is Three Little Birds better than hiring a business plan consultant?",
    a: "For a rigorous first draft: yes. A consultant charges $2,000–$5,000 and takes 2–4 weeks; Three Little Birds drafts a 14-section plan in about 60 seconds and iterates instantly as you refine your inputs. Consultants win for deeply specialized industries (biotech, aerospace, regulated finance) where domain intuition matters most. For 95% of small-business founders, the Three Little Birds-drafted plan + Health scores + lender package is faster, cheaper, and more actionable.",
  },
  {
    q: "Do you offer annual pricing or a discount for paying yearly?",
    a: "Yes — annual billing saves one month on every tier. Basic drops to $150/year (vs $180 monthly), Standard to $250/year (vs $300), and Advanced to $350/year (vs $420). Switch between monthly and annual any time from Billing.",
  },
  {
    q: "Do you offer student, nonprofit, or startup discounts?",
    a: "Not on the standard tiers today, but we have a founder-forever offer inside the app: any user who completes their first plan and gets to Standard-tier Plan Health ≥ 60 unlocks a 20% permanent discount on Standard for referring one paying founder. For registered nonprofits, email hello@threelilbrds.com with your 501(c)(3) letter and we'll apply a 50% credit manually.",
  },
  {
    q: "Can I cancel any time? Will I be refunded?",
    a: "Yes — cancel any time from Billing → Manage subscription. Cancellation is effective at the end of your current billing period (monthly or annual). We refund unused portions of annual plans on a pro-rated basis within the first 30 days; after that, the annual plan runs to the end of its term. Cancelled or lapsed accounts keep their data indefinitely, so you can resubscribe and pick up where you left off.",
  },
  {
    q: "What payment methods do you accept?",
    a: "All major credit and debit cards (Visa, Mastercard, Amex, Discover) via Stripe. Apple Pay and Google Pay are also supported on the checkout page. We don't currently accept ACH, wire, or crypto — but if you're a nonprofit or education customer we can invoice manually. Email billing@threelilbrds.com.",
  },
  {
    q: "Can I try the paid features before I subscribe?",
    a: "Yes — every new account gets a 7-day free preview of the Standard tier with no credit card required. During the preview you get full access to compliance, grants, mentor, plan health, and budget tracking; five sections (Competitive Analysis, Customer Acquisition, Infrastructure, Pricing Model, 3-Year Revenue Projection) show sample content until you subscribe, then unlock your real Three Little Birds-generated numbers instantly.",
  },
  {
    q: "Is Three Little Birds a good fit for a first-time founder with no business background?",
    a: "Yes — it's exactly who the product was built for. The 5-step wizard uses plain-English questions (no jargon), Three Little Birds writes every plan section for you, and the Business Hub literally names the single move that will most improve your business right now. If you don't know what a SWOT analysis is or how to compute break-even, that's fine: the app does that math for you and explains what each number means.",
  },
  {
    q: "Can I use this if I already run an existing business (not launching a new one)?",
    a: "Yes — Three Little Birds has an Operate mode for existing businesses. Instead of Launch Health, you'll see Operating Health (revenue trend + financial health + marketing + launch health), a feasibility analyzer to pressure-test new products against real unit economics, an optimization panel that highlights the highest-leverage cost and pricing moves, and a continuous-improvement tracker for measured process changes over time.",
  },
  {
    q: "Does Three Little Birds work for restaurants, cafes, and food-service businesses?",
    a: "Yes — food & beverage is one of the most-used industries in the product. The compliance tracker auto-seeds jurisdiction-specific food-service items (health department, retail food license, food-handler certification, liquor if applicable). The budget templates seed COGS, labor, rent and utilities line items typical of the industry, and the industry-benchmark strip compares your food-cost %, labor %, and prime cost against vetted restaurant ranges.",
  },
  {
    q: "Can I use Three Little Birds for a SaaS or software startup?",
    a: "Yes — SaaS is well-supported. The driver-based forecast (Advanced) models MRR, ARR, churn, and CAC payback from real drivers (units × price × monthly growth, per-role headcount), the CRM handles a B2B deal pipeline (Lead → Qualified → Proposal → Negotiation → Won/Lost), and the investor marketplace lets you flip a switch when your Plan Health crosses 50 to let matched investors browse your plan.",
  },
  {
    q: "Does the app work for e-commerce or Shopify stores?",
    a: "Yes — e-commerce is a first-class industry in the wizard. The Three Little Birds-drafted competitive analysis pulls DTC and marketplace comparables, the pricing model wizard handles bundles and tiered offers, and the driver forecast lets you model units × AOV × conversion × traffic. Budget templates seed shipping, fulfillment, ad spend, and platform fees typical of a Shopify or WooCommerce store.",
  },
  {
    q: "Can I use Three Little Birds for a consulting or services business?",
    a: "Yes — consulting is fully supported. Product & Service catalog handles retainer, fixed-fee, and hourly offerings side-by-side, the CRM tracks each engagement through the deal pipeline, and the driver forecast models revenue as billable-utilization × rate × active-clients. The 3-statement financial package and lender-ready PDF work identically to a product business, and the compliance tracker surfaces state-level occupational licensing when applicable.",
  },
  {
    q: "Can I use this to plan a nonprofit or a social enterprise?",
    a: "Yes — nonprofits and social enterprises use the same wizard. The grants finder is especially useful (federal, state, local + private and foundation programs), the budget module handles restricted vs unrestricted funds via categories, and the plan sections adapt to a mission-first framing when you describe your business as nonprofit or B-corp in Step 1. Registered 501(c)(3)s should email us for a manual credit — see the discount question above.",
  },
  {
    q: "Do I need a US business address to use Three Little Birds?",
    a: "No — the platform is available to founders in any country. Federal-baseline compliance items are US-centric today (EIN, IRS, EEOC, FLSA, etc.), but the LLM-generated compliance items adapt to the geography you enter, and the plan / budget / roadmap / grants finder / CRM / documents / campaigns work identically regardless of country. We're rolling out a UK and Canada compliance seed pack in H1 2026.",
  },
  {
    q: "Can I switch the app language away from English?",
    a: "Not yet — the UI is English-only today. However, you can write your plan inputs in any language (Three Little Birds will draft your plan in whatever language you use in the questionnaire) and the exported PDF/DOCX will match. Native Spanish, French, and Portuguese UI translations are on the H2 2026 roadmap.",
  },
  {
    q: "Does the plan explain what business entity to choose (LLC, S-corp, sole prop)?",
    a: "Yes — the Founder Mentor and the Compliance tracker both weigh in. Ask the Mentor 'which entity should I form?' and it walks you through the tradeoffs based on your revenue projection, personal investment, hiring plans and state. The Compliance tracker then seeds the specific formation and reporting items for whichever entity you pick (Beneficial Ownership Information for LLCs and corps, quarterly estimated tax for sole props, etc.).",
  },
  {
    q: "Will Three Little Birds file my LLC or business registration for me?",
    a: "No — we don't file paperwork directly. Instead, the roadmap board's Find Services feature surfaces vetted providers (Stripe Atlas, ZenBusiness, LegalZoom) for that exact task and opens an in-app agent that guides you through their exact flow. You do the filing; we make sure you know what to file, in what order, and where.",
  },
  {
    q: "Can I connect Stripe to accept payments through the app?",
    a: "Not for direct checkout inside Three Little Birds — the app is a planning and operating tool, not a payments processor. But when you're ready to accept payments, the roadmap's Find Services feature deep-links you to Stripe Atlas for incorporation-plus-Stripe setup, and the CRM captures won deals so your forecast stays in sync with real revenue.",
  },
  {
    q: "Does Three Little Birds integrate with QuickBooks Online?",
    a: "Yes — on Standard and Advanced you can connect QuickBooks Online in one click from Settings → Accounting integrations. We pull the last 30 days of transactions strictly read-only (we never write anything back) and merge them into your Budget Actuals per month under a single Imported (QBO) line. Xero is also supported the same way; disconnect any time and we delete your tokens immediately.",
  },
  {
    q: "Does Three Little Birds integrate with Xero?",
    a: "Yes — Xero is supported identically to QuickBooks Online. Standard and Advanced users can connect Xero in one click from Settings → Accounting integrations and pull the last 30 days of transactions into Budget Actuals. Read-only, never writes back, and one-click disconnect deletes the OAuth tokens.",
  },
  {
    q: "Do you support Google Sheets, Excel, or CSV import for budgets?",
    a: "Yes — every tier supports CSV import of budget lines. Open Budget → Import CSV and map your columns (category, planned amount, actual amount, month) — the guided importer catches header mismatches and previews the first 5 rows before you commit. If you already use QuickBooks or Xero (Standard+), the direct integration skips the CSV middleman entirely.",
  },
  {
    q: "Can I export my plan to Microsoft Word or Google Docs?",
    a: "Yes — every paid tier can export a full plan as a .docx (Microsoft Word compatible, opens cleanly in Google Docs). The DOCX matches your Brand Kit logo, tagline, and colors. Alongside DOCX, you can export the plan as PDF (watermarked on Basic, clean on Standard+), the pitch deck as PDF or PowerPoint (Standard+), and the driver forecast as a 36-month CSV (Advanced).",
  },
  {
    q: "Can I export my pitch deck as PowerPoint?",
    a: "Yes — Standard and Advanced users can export the pitch deck as either PDF or PowerPoint (.pptx). The PowerPoint file is fully editable in PowerPoint, Keynote, and Google Slides. Slide order and toggled slides in Plan actions → Customize deck are honored on both formats, and the market-research slide (if generated) is included with clickable source citations.",
  },
  {
    q: "Does Three Little Birds work on mobile?",
    a: "Yes — the app is fully responsive and works in any modern mobile browser (Safari on iOS, Chrome on Android). The 5-step wizard, plan reading, health scores, budget check-ins, CRM contacts, and receipt scanning all work on mobile. There isn't a native iOS/Android app yet, but the mobile web experience is a first-class citizen (roughly 30% of our founders launch their plan from a phone).",
  },
  {
    q: "Does the app work offline?",
    a: "No — Three Little Birds requires an internet connection. The drafting, health scoring, grants finder, and financial calculations all run on our servers. Once a section is drafted it caches for fast re-reads, but any edit or regeneration needs an active connection.",
  },
  {
    q: "Where is my business data stored, and is it encrypted?",
    a: "All data is stored in a managed MongoDB cluster in US-East (AWS), encrypted at rest (AES-256) and in transit (TLS 1.3). Backups are taken daily and retained for 30 days. We do not train models on your data, we do not sell or share your data with third parties, and we do not use your plan content to improve model prompts for other users.",
  },
  {
    q: "Is Three Little Birds GDPR-compliant?",
    a: "Yes — we honor GDPR data subject rights for EU users. Ask via privacy@threelilbrds.com for data access, correction, deletion, or portability requests and we'll respond within 30 days. Our data-processing agreement is available on request. Sub-processors (Stripe, Perplexity, our email + SMS delivery providers, our LLM providers) are all GDPR-DPA covered.",
  },
  {
    q: "How do I delete my account and all my data?",
    a: "Open Settings → Account → Delete account. This immediately deletes your user record, all business plans, budgets, roadmaps, CRM contacts, documents, and Grants applications. Deletion is irreversible and takes effect within 24 hours across primary and backup replicas. If you'd rather export your data first, hit Settings → Export all data.",
  },
  {
    q: "Do you support two-factor authentication (2FA)?",
    a: "Not yet on password-based accounts — 2FA is on our H1 2026 roadmap. In the meantime, Google Sign-In is available on the login page and enforces whatever 2FA you have set on your Google account (Authenticator, security key, or SMS). For extra security use Google Sign-In today.",
  },
  {
    q: "Can I invite my cofounder, accountant, or advisor to my plan?",
    a: "Yes — on Standard and Advanced, click Share on any plan and invite collaborators by email. Choose a role: Editor (edit sections, budget, roadmap), Commenter (leave comments, no edits), or Viewer (read-only). You can also generate a public read-only link for external stakeholders. Invited collaborators don't count against your seat cap; they need their own Standard+ account only if they want to run their own plans.",
  },
  {
    q: "What's the difference between the Standard and Advanced tiers?",
    a: "Standard ($25/mo) is the full launch OS: plan, roadmap, budget, compliance, grants, CRM, documents, lender-ready package, benchmarks, investor marketplace, and pitch deck. Advanced ($35/mo) adds driver-based 3-year monthly forecasting (36-month CSV export), what-if scenario planning, investor scenarios (equity/income/hybrid), Email & SMS campaigns, unlimited business plans, and priority support. Advisor / white-label tiers start at $149/mo for firms serving multiple client founders.",
  },
  {
    q: "Do you offer a white-label option for accounting or consulting firms?",
    a: "Yes — Advisor / white-label tiers put your logo and colors on client PDFs, decks, and share cards, with a co-branded client login page, a cohort dashboard with per-client health scores, custom-subdomain support (plan.youragency.com via CNAME + TXT), and full Standard features on every client seat. Advisor Starter is $149/mo (10 client seats), Advisor Pro is $349/mo (unlimited).",
  },
  {
    q: "Is there priority support on the Advanced plan?",
    a: "Yes — Advanced customers get priority email support with a target 4-hour first-response time on business days (Basic and Standard target 24 business hours). Every tier also has the in-app Founder Mentor available 24/7 for immediate answers, and the /help center is free and unauthenticated at /faq for public search.",
  },
  {
    q: "How do I improve my Plan Health score?",
    a: "Open the Health Check tile on your plan and read the What drives the score panel — it shows your four deterministic components (Runway, Budget completeness, Revenue realism, Roadmap balance) as progress bars, each with plain-English reasoning. Fix the weakest one first: if Roadmap balance is 8/25, ship a couple of To-Do tasks; if Revenue realism is low, tighten your Y1 target or your pricing; if Budget is incomplete, log a month of actuals. Re-run Health as often as you want (no cap).",
  },
  {
    q: "What's a good Plan Health score to have before pitching investors?",
    a: "Investors on our marketplace only see plans with Plan Health ≥ 50 (that's the switch to flip on the Investor tab). Practically: 60+ signals a coherent early-stage story with realistic runway and a shipping roadmap; 75+ signals a plan ready for a real check. Founders who close funding on the platform average 68 at listing time. Below 50, spend another week tightening the fundamentals before opening the door.",
  },
  {
    q: "Can Three Little Birds help me find investors or angel funding?",
    a: "Yes — Standard+ founders can flip a switch to list their plan on the Investor Marketplace. Investors who match your industry and stage browse a curated read-only directory of listed plans. You see per-section engagement counts — which parts of your plan are pulling investor attention this week — so you know exactly what to tighten. This is investor discovery, not a broker: you still run your own outreach and negotiations.",
  },
  {
    q: "Does Three Little Birds help with SBA loans?",
    a: "Yes — the Lender-ready financial package (Standard+) generates the exact documents an SBA lender asks for: a 3-statement projection (P&L, cash flow, balance sheet that balances), Sources & Uses, break-even, DSCR by year, an Assumptions page, plus blank borrower forms (Personal Financial Statement, Debt Schedule, Collateral Schedule). Enter the loan amount, term, APR, and equity injection; the statements are formula-driven so they tie out. You still supply tax returns, bank statements, and personal credit.",
  },
  {
    q: "How does the grants finder actually work — where do the grants come from?",
    a: "The grants finder pulls from four live sources in parallel: Grants.gov (federal open opportunities via the official API), SAM.gov Assistance Listings (federal authoritative catalog), Perplexity Sonar (a web-cited search that surfaces state, city, county, and private/foundation/corporate programs with real clickable source URLs), and a Three Little Birds matcher that scores every hit against your business profile. Every match is tagged by source, and you can generate a per-grant application draft directly inside the app.",
  },
  {
    q: "How long does it take to create my first plan?",
    a: "About 15 minutes end-to-end. The 5-step wizard takes 2–3 minutes to answer, Three Little Birds takes 30–60 seconds to draft your 14 plan sections, and the remaining time is spent reading the draft and tightening any section you want. Roadmap, health score, and (on Standard+) compliance and market research auto-populate in the background so you don't have to click Regenerate on each.",
  },
  {
    q: "Can I have multiple businesses on one account?",
    a: "Yes — Basic supports 5 plans total, Standard supports 20 plans, Advanced is unlimited. Each plan gets its own roadmap, budget, compliance tracker, CRM pipeline, and health scores. Only 3 plans can be Active (editable) at once on paid tiers (1 on Basic and free); inactive plans are read-only until you flip Activate — this keeps founders from spinning up 20 half-built plans and losing focus.",
  },
  {
    q: "What are Service Agents and how do they help me actually get things done?",
    a: "Service Agents are in-app Three Little Birds assistants that sit inside every roadmap task and walk you through a real vetted vendor — Stripe Atlas or ZenBusiness for filing your LLC, Mercury for banking, Gusto for payroll, Shopify for a storefront, Canva or Looka for branding, Calendly for scheduling, HubSpot for CRM, Mailchimp for email, and 15+ more across legal, finance, HR, design, marketing, product and ops. Each agent knows ONE task and ONE vendor, so instead of a generic chatbot you get step-by-step guidance for that exact filing / signup / setup. Two modes: Chat (ask anything about that vendor for that task) and Quick (answer 3–5 starter questions and the agent generates a ready-to-paste draft — an EIN filing summary, a business account application checklist, a payroll onboarding brief). Available on Standard and Advanced from any roadmap card's Get-it-done agents panel.",
  },
];


// ---- Knowledge Base ----
// Each article: { slug, title, category, summary, body (markdown-ish, whitespace-preserved) }

export const KB_CATEGORIES = [
  { key: "getting-started", label: "Getting started" },
  { key: "plan", label: "Business plan" },
  { key: "execution", label: "Execution & roadmap" },
  { key: "money", label: "Money & funding" },
  { key: "ai", label: "Founder Mentor & voice" },
  { key: "account", label: "Account & billing" },
];

export const KB_ARTICLES = [
  {
    slug: "market-research-live-citations",
    title: "Market research with live citations (Standard+)",
    category: "plan",
    summary: "Pull a web-sourced market report into your plan — TAM/SAM/SOM, trends, competitors and demand signals — with every figure backed by a real, clickable source. Rides into your pitch deck too.",
    body: `# Market research with live citations

Standard and Advanced plans add **live-web market research** directly inside your Business Plan — no copy-paste from search engines, no made-up numbers. It's powered by a live-search Three Little Birds (Perplexity Sonar), so every figure links back to a **real source you can open and verify**.

---

## Where to find it

Open any Business Plan and scroll to the **Market analysis** section (or tap *Market analysis* in the left rail). The research lives **right under** Three Little Birds market-analysis block, in a panel titled *"The market, with receipts."* There's no separate page — it's part of your plan.

## What it gives you

- **Market sizing** — **TAM / SAM / SOM** with a one-line basis for each figure.
- **Growth trends** — the strongest current movements in your market, with direction/numbers.
- **Competitor landscape** — the most relevant direct competitors, their positioning, and recent moves.
- **Demand signals** — concrete evidence buyers want this (search, hiring, funding, adoption).
- **Recommendations** — 3-5 specific, high-leverage actions for your business.
- **Sources** — a numbered list of clickable citations behind the report.

## How to run it

Hit **Generate research** (or **Regenerate** to refresh). It takes ~15-25 seconds while it searches the live web. Each run uses a small research credit, so generate when you want a fresh pull rather than on every visit.

## It rides into your pitch deck

When a report exists, a **"Market research" slide** is automatically added to your **Pitch deck** — both the PDF and the editable **PowerPoint (.pptx)** export — with your TAM/SAM/SOM, top trends, and the top sources. Investors and bankers see the market sizing *with receipts* without leaving the deck.

## Where to find it

- In the plan: **Market analysis** section → *"The market, with receipts"* panel.
- Tier: **Standard or Advanced** (free / Basic see an upgrade prompt).`,
  },
  {
    slug: "crm-sales-pipeline",
    title: "CRM & sales pipeline (Standard+)",
    category: "execution",
    summary: "Track contacts, companies and a drag-and-drop deal pipeline that feeds your Goals and Runway — plus a workspace-wide CRM Dashboard.",
    body: `# CRM & sales pipeline

Standard and Advanced plans include a **native CRM** on every business — no third-party tool required. It lives in two places: a full module per plan, and a Pipeline summary embedded in the Business Plan itself.

---

## What you can track

- **Contacts** — people, with inline email + phone validation and formatting.
- **Companies** — accounts you're selling to, with industry tags.
- **Deals** — opportunities moving through a drag-and-drop pipeline: **Lead → Qualified → Proposal → Negotiation → Won / Lost**. Each deal carries a value and currency.

## It feeds the rest of your plan

The CRM isn't a silo. Closed-**won** deals automatically:

- **Advance your Goals** — revenue-type goals sync their progress from won-deal value.
- **Extend your Runway** — closed-won sales appear as a capital-stack line in the Budget/Runway calculation, with a projected overlay for open pipeline.

## Pipeline section inside the plan

Open any Business Plan and scroll past **Funding strategy** to the **Pipeline** section (or click *Pipeline* in the left rail). You'll see tiles for open deals, pipeline value, won deals and won value, a **stage mini-bar** showing your funnel distribution, and contact/company counts — with one-click links to the full CRM and the dashboard. In investor / public view this section is **read-only**.

## Workspace CRM Dashboard

Go to **/crm** (or *CRM* in the top nav) for a cross-plan rollup of every opportunity: deals-by-stage, deals-over-time and deals-by-plan charts, a **win-rate %** tile, a **created-date range filter**, and **CSV export** of the filtered opportunities for board decks.

## Where to find it

- Per plan: the **Plan-actions** menu (floating panel on the plan) → *CRM*, or the *Pipeline* row in the plan's left rail.
- Workspace-wide: **/crm**.
- Tier: **Standard or Advanced** (free / Basic see an upgrade prompt).`,
  },
  {
    slug: "document-library",
    title: "Document Library (Standard+)",
    category: "execution",
    summary: "A per-plan and workspace-wide vault for contracts, invoices, licenses and logos — with folders, tags, secure downloads and research of images + PDFs.",
    body: `# Document Library

The Document Library (Standard and Advanced) is where your business paperwork lives — contracts, invoices, IDs, licenses, brand assets. Files are stored in secure object storage, never in the browser.

---

## Organize

- **Folders** — group files (e.g. Legal, Finance, Brand).
- **Tags** — add topical tags to any file. In the workspace Library, **click any tag to instantly filter** to every document with that tag; a chip shows the active filter and clears it in one click.
- **Search** — by filename, plan, Three Little Birds summary or tag.

## Three Little Birds Analyze (images + PDFs)

Click the **Analyze** (sparkle) action on any image **or PDF**. Three Little Birds reads the document — OCR for images, text extraction for PDFs — then auto-generates a plain-English **summary**, classifies the **document type** (invoice / receipt / contract / id / letter / report), and suggests **tags**.

## Per-plan vs workspace-wide

- **Per plan**: open from the plan's **Plan-actions** menu → *Documents*. Manages just that business's files.
- **Workspace-wide**: **/documents** (or *Documents* in the top nav) aggregates files across every plan, with a **grid or table** view toggle (your choice is remembered), per-row download / analyze / delete, and total storage usage.

## Safe by design

Downloads use secure blob streaming and deletions are confirmed (soft-delete), so nothing disappears by accident.

## Where to find it

- Per plan: **Plan-actions** menu → *Documents*.
- Workspace-wide: **/documents**.
- Tier: **Standard or Advanced**.`,
  },
  {
    slug: "email-sms-campaigns",
    title: "Marketing — Campaigns & Journeys (Advanced)",
    category: "execution",
    summary: "Turn your contacts into outreach — reusable Email & SMS templates with rich placeholders, multi-step Campaign Journeys, per-segment Send Budgets, and a workspace-wide Marketing dashboard.",
    body: `# Marketing — Campaigns & Journeys

Marketing (an **Advanced** feature) lets you reach your audience straight from a business plan or the workspace-wide dashboard. Two ways in:

- **Per plan** — *Plan-actions → Marketing*, or **/business/{id}/campaigns**.
- **Cross-plan rollup** — *Tools → Marketing* in the top nav, or **/marketing** — one screen aggregating every campaign and journey across every plan you own, with per-business filter and quick-jump links.

---

## 1. Personalization placeholders

Every email and SMS composer (Campaigns AND Journey nodes) surfaces a **Placeholders** chip strip just below the field. Click any chip to insert the token at your caret, or copy it to clipboard. Supported tokens:

- **{{first_name}}** — contact's first name (fallback: *there*)
- **{{name}}** — full name (fallback: *there*)
- **{{email}}** — email address
- **{{phone}}** — phone number
- **{{company}}** — company name
- **{{business_name}}** — your plan's business name (fallback: *our business*)

Missing fields fall back to a sensible default so the message stays natural — an empty first-name renders as *there* instead of leaving a raw {{token}} in the message.

## 2. Templates (reusable copy)

On the **Templates** tab, save Email or SMS templates you'll reuse. The Journey editor's *Message Template Picker* pulls from the same list, so a template you build in Campaigns is immediately available in a Journey step.

## 3. Campaigns (one-off blasts)

On the **Campaigns** tab, click **New campaign**:
- Pick the channel (Email or SMS), optionally start from a template.
- Write your message with placeholders and an optional CTA link — links are rewritten so clicks are tracked.
- Choose **Send manually** (draft that fires when you hit *Send now*) or **Auto-send** on a start/end window.
- Open **Recipients** to pick from CRM contacts (filtered by email/phone availability) or add ad-hoc recipients. Duplicates de-duped.

## 4. Journeys (multi-step nurture)

Journeys chain **send-email · send-sms · wait · split · mv-split · webhook · end** nodes into a single automation. Trigger options:
- **Manual** — you enrol contacts yourself.
- **New contact** — auto-enrol every new contact.
- **Segment or Group entry** — pick either a built-in **Smart Segment** (New this month, No activity 60d+, Has email, Has phone, Responded, Not responded, Opened message, Clicked links) OR one of your saved **Contact Groups**. The system checks membership every ~10 min and auto-enrols contacts that newly match — existing members at activation are baselined so nobody gets a spam blast.

Journey nodes support the same **{{placeholder}}** contract as Campaigns. A live **Cost Guard** below the editor forecasts projected month-to-date sends before you hit *Activate*.

## 5. Send Budgets (never blow your quota)

Founder-level monthly caps live at **Settings → Send Budget** (default 5,000 email / 500 SMS per month). Admins can override per-user from the Admin dashboard.

- **Global cap** — hard ceiling for the whole month, across every campaign and journey.
- **Per-segment sub-caps** (Phase 10) — carve the global cap into per-segment budgets (e.g. VIP group gets 800 emails, cold list gets 100). Managed at *Settings → Per-segment sub-caps* (founder) or the *Manage user* modal (admin). Sub-caps NEVER exceed the global cap and blank fields fall back to it. When a Journey targets a specific segment, the Cost Guard enforces min(global_remaining, segment_remaining) — the lower one wins.

If activate would exceed either cap, we return a friendly 409 with the exact segment / channel that would be breached so you can raise the cap or shrink the audience.

## 6. Send & track

Every campaign card shows live **Sent · Delivered · Opened · Clicked** counters plus open + click rates. Email opens use an invisible pixel; clicks use a tracked redirect. The **Journey Send Ledger** shows month-over-month send volume so founders can spot spikes.

## Delivery notes

- **Email** — delivered live through our email provider, with real open + click tracking.
- **SMS** — sandbox by default; connect a supported SMS provider to flip real sends on. Add your provider credentials to your backend .env and point the delivery-status webhook at /api/sms/status, and the app updates Delivered / Failed counters as each message is confirmed. (SMS has no native *open* concept — SMS cards show Delivered + Clicked instead of Opened.)

## Where to find it

- **Per plan** — *Plan-actions → Marketing*, or **/business/{id}/campaigns**.
- **Cross-plan rollup** — *Tools → Marketing* in the top nav, or **/marketing**.
- **Journeys** — same plan, *Plan-actions → Journeys*, or **/business/{id}/journeys**.
- **Budgets** — *Settings → Send Budget* + *Settings → Per-segment sub-caps* for founders, *Admin → Send Budgets* for admins.
- **Related** — *Plan-actions → Marketing tools → Ad Copy Studio / Ads Tracking / Attribution* for the paid-ads loop (see the two KB articles below).
- **Deep-dive on the canvas builder** — see the *Marketing Journeys — canvas builder + Cost Guard* KB article.
- **Tier** — **Advanced**.`,
  },
  {
    slug: "marketing-journeys",
    title: "Marketing Journeys — canvas builder + Cost Guard (Advanced)",
    category: "execution",
    summary: "The drag-and-drop canvas that chains email, SMS, wait, split and end nodes into one live automation. Fires on manual enrolment, new-contact creation, smart-segment entry or Contact-Group entry — with a real-time Cost Guard that blocks any send that would blow your monthly budget.",
    body: `# Marketing Journeys

Marketing Journeys (an **Advanced** feature) is the canvas builder that stitches Email + SMS + Wait + Split into a single live automation. If a one-off blast is a *Campaign*, a scheduled multi-touch nurture is a *Journey*.

Open one from **Plan-actions → Journeys**, or **/business/{id}/journeys**.

---

## 1. The five node types

- **Email** — send a rich email using a saved template or ad-hoc copy. Placeholders like {{first_name}} + {{business_name}} are click-to-insert from a chip strip below the composer.
- **SMS** — send an SMS (via your configured SMS provider if connected, otherwise sandboxed) with the same placeholder support.
- **Wait** — pause for N minutes / hours / days before the next node. Useful for the classic *Welcome → wait 2d → Follow up* pattern.
- **Split** — branch on a boolean condition (opened previous email? clicked? tagged with a specific segment?). Each side of the split is its own downstream flow.
- **End** — explicitly terminate the journey for that contact so they never re-enter this specific branch.

Drag nodes from the palette onto the canvas, wire them up, and hit **Activate** — the journey is live from that moment on.

## 2. Four ways to trigger a journey

- **Manual** — you enrol contacts yourself from the CRM or the Journeys tab.
- **New contact** — every new contact in this plan is auto-enrolled.
- **Smart segment entry** — auto-enrol anyone who newly matches one of the 8 built-in segments (*New this month · No activity 60d+ · Has email · Has phone · Responded · Not responded · Opened message · Clicked links*). Membership is re-checked every ~10 minutes; contacts already in the segment at activation are baselined so nobody gets a retroactive spam blast.
- **Contact Group entry** — same as segment entry, but for the founder-authored Contact Groups (saved static or rule-based lists).

## 3. Cost Guard — never blow your quota

Below the editor, the **Cost Guard** panel forecasts every send from every active node against:

- **Global monthly Send Budget** — hard ceiling (default 5,000 email / 500 SMS/month, override at *Settings → Send Budget*).
- **Per-segment sub-caps** — optional carve-outs so a VIP list can't be starved by a cold-list drip. Live at *Settings → Per-segment sub-caps*.

If Activate would push either cap over the edge, the API returns a **friendly 409** naming the exact segment + channel that would breach — so you can raise the cap, shrink the audience, or wait til next month.

## 4. Live stats + Send Ledger

Every journey card surfaces **Sent · Delivered · Opened · Clicked** counters per node, plus open + click rates. The **Journey Send Ledger** on the Marketing dashboard rolls month-over-month send volume across all journeys and plans so a spike is instantly visible.

## 5. Delivery notes

- **Email** — delivered live through our email provider. Opens use an invisible pixel; clicks route through a tracked redirect.
- **SMS** — sandboxed by default. Connect a supported SMS provider (add your credentials to your backend .env and point the delivery-status webhook at */api/sms/status*) to flip real sends on. Delivered / Failed counters update as each message is confirmed.

## Where to find it

- **Journey canvas** — *Plan-actions → Journeys*, or **/business/{id}/journeys**.
- **Templates + Campaigns + placeholders** — the sibling *Marketing — Campaigns & Journeys* KB article.
- **Send budgets** — *Settings → Send Budget* + *Settings → Per-segment sub-caps*.
- **Tier** — **Advanced**.`,
  },
  {
    slug: "ad-copy-studio",
    title: "Ad Copy Studio — Three Little Birds-drafted Social Media Ads for Meta, Google, TikTok & LinkedIn (Advanced)",
    category: "execution",
    summary: "Generate platform-native social media ad copy in seconds. Pick a product, tone and audience — the studio drafts 3 variants per platform with headlines, primary text, CTAs, hashtags and character-limit guards, then exports as CSV with UTM tags auto-appended so every click routes into your CRM attribution.",
    body: `# Ad Copy Studio · Social Media Ads

Ad Copy Studio (an **Advanced** feature) drafts ready-to-launch **social media ad copy** for **Meta, Google, TikTok and LinkedIn** from your existing plan — no blank composer, no character-count guesswork.

---

## Where to find it

- **Per plan** — *Plan-actions → Marketing tools → Ad Copy Studio*, or **/business/{id}/ad-copy**.
- **From Campaigns** — the "Ad Copy Studio" button in the Campaigns page header.

## How it works

Pick one of your active **Products or services**, a target audience (segment or free-text), and a tone (Bold · Friendly · Expert · Playful). Optionally add demographic hints (age range, gender, region).

The studio calls the LLM once and returns **3 platform-tailored variants per platform**:

- **Meta** — headline (40 char) · primary text (125 char) · CTA
- **Google** — 3 headlines (30 char each) · 2 descriptions (90 char each)
- **TikTok** — hook · body · caption (with hashtag suggestions)
- **LinkedIn** — headline (150 char) · intro (600 char) · CTA

Each field ships with a **character-limit guard** — if the LLM overshoots we auto-truncate and flag the variant so you can hand-edit before export.

## Export with UTMs baked in

Every draft has a **Copy CSV** button. The CSV includes a **landing URL** column with UTM tags auto-appended per variant — the tags are utm_source (platform), utm_medium=paid, utm_campaign (draft id), and utm_content (variant index).

Point your ad's destination URL at this pre-tagged link and every click routes through a per-variant UTM. When the resulting deal lands in the CRM, Three Little Birds **parses the UTM automatically** and links the deal back to the exact draft × variant that produced it (see the *Ads Tracking + Attribution* KB article).

## Regenerate + rate

Each draft has a **Regenerate** button (costs a small Three Little Birds credit) and a **thumbs up / down** rating that the studio uses to bias future generations toward what's worked for you.

## Where to find it

- Per plan: **/business/{id}/ad-copy** (Plan-actions → Marketing tools).
- Cross-plan: The Campaigns page header on every plan links to it.
- Tier: **Advanced**.`,
  },
  {
    slug: "ads-tracking-attribution",
    title: "Ads Tracking + CRM Attribution — close the loop from ad → deal → revenue (Advanced)",
    category: "execution",
    summary: "Paste your Meta Pixel + Google Ads conversion IDs once. Every closed-won deal can be attributed to a specific Ad Copy Studio draft × variant — automatically via UTMs, manually via a picker, or in bulk via multi-select. Plus a 0–100 Attribution Health Score, a 12-week sparkline trend, filter pills for one-tap drill-through, and a 24-hour Undo on every bulk action.",
    body: `# Ads Tracking + CRM Attribution

Ad Copy Studio is only half the loop — **Ads Tracking + Attribution** closes it. It answers *"which ad drove that revenue?"* by tracing every closed-won deal back to the exact Ad Copy Studio draft × variant that produced it.

Available on **Advanced**.

---

## 1. One-time tracking setup

Open **Plan-actions → Marketing tools → Ads Tracking** (or **/business/{id}/ads-tracking**). Paste:

- **Meta Pixel ID** — from Meta Events Manager
- **Google Ads Conversion ID + Label** — from Google Ads
- **Landing domain** — the site the ad points to
- *(Optional)* **Meta Access Token + Test Event Code** — enables Layer C server-side Purchase events (see §4)

Three Little Birds regenerates a copy-paste **head-tag snippet** with the Meta Pixel bootstrap + gtag config + a *window.tlbTrackConversion()* helper. Paste it once into your site's header. Done.

## 2. Automatic UTM attribution

Every Ad Copy Studio CSV export appends UTM tags to the landing URL — utm_source (platform), utm_medium=paid, utm_campaign (draft id), utm_content (variant index).

When a lead lands on your site and later becomes a deal in the CRM, you can attribute it in **three ways**:

- **Paste-URL mode** — drop the inbound click URL into the deal drawer's Attribution row and Three Little Birds parses the UTM tags for you.
- **Manual picker** — flip the mode to *Pick manually*, choose a draft, choose a variant. Great for deals that came in via phone or email but you know the campaign.
- **Bulk attribute** — on the CRM Pipeline, click **Bulk attribute**, tick as many deal cards as you like, then pick one draft × variant to stamp on all of them in a single write.

## 3. Bulk Clear + 24-hour Undo

Same multi-select bar carries a **Clear attribution** action so you can wipe a mis-tagged batch in one write. Every bulk action (attribute or clear) opens an **Undo** toast — the server snapshots the prior attribution and gives you a **24-hour window** (72h on Advanced tier by default) to restore in one click. Admins can tune the per-tier undo window at **/admin/attribution/undo-windows**.

## 4. Meta Conversions API (Layer C · server-side Purchase)

When a deal flips to **Won** and it has an attributed draft, Three Little Birds fires a server-side **Purchase** event to Meta's Conversions API — SHA-256-hashed email/phone, deal value as revenue, deal ID as the event_id so it dedups against your client Pixel. Bypasses ad-blockers and iOS limits so your bidding optimises for **real closed customers**, not just form fills.

A **Replay to Meta CAPI** button on the Attribution page backfills every Won-attributed deal — useful right after you first paste the token.

## 5. Attribution Leaderboard + Health Score

Two visual surfaces summarise the loop:

- **Attribution Leaderboard** — Top 5 ads by closed-won revenue with a proportional bar, platform pill, won/attr counts and a totals footer. Mounts **above the CRM tabs** AND inside the Business Plan's Pipeline section (compact variant).
- **Attribution Health Score** — a 0–100 chip next to the leaderboard title. Blends *coverage* (drafts closing any deal × 30) + *win rate* (won ÷ attributed × 40) + *revenue efficiency* (avg-per-won ÷ max-per-won × 30). Tone flips red / amber / green at 40 / 70 breakpoints. Hover for the formula.
- **12-week history sparkline** — inline SVG line + area + ▲/▼/• delta chip so a struggling week is instantly visible.

## 6. Filter Pills + Drill-through

Above the plan's Pipeline tiles sits a **Filter pills** strip — your top 6 ads as one-tap pill chips. Tap any to jump straight to the CRM pipeline **already filtered** to only the deals attributed to that draft. A 3-way sort toggle lets you flip the pills between **Revenue** · **Win rate** · **Recent** depending on what you're diagnosing.

On the pipeline itself, a bright indigo *"Showing N deals attributed to <PLATFORM · id>"* chip announces the filter loudly with a **Clear filter** button so a filtered view is never mistaken for an empty pipeline.

## 7. Reports & Replay

The **Attribution report** (Plan-actions → Marketing tools → Attribution, or **/business/{id}/ad-copy/attribution**) aggregates every draft × variant: attributed deals, won deals, attributed revenue, won revenue, plus per-variant breakdowns. Drafts with zero attribution are hidden so the report stays honest.

## Where to find it

- **Ads Tracking setup** — Plan-actions → Marketing tools → **Ads Tracking**, or **/business/{id}/ads-tracking**.
- **Attribution report** — Plan-actions → Marketing tools → **Attribution**, or **/business/{id}/ad-copy/attribution**.
- **CRM leaderboard + pipeline + bulk actions** — the CRM page (/business/{id}/crm) and the Business Plan's Pipeline section.
- **Admin undo tuning** — **/admin/attribution/undo-windows**.
- **Tier** — **Advanced**.`,
  },
  {
    slug: "industry-benchmarks",
    title: "Industry benchmarks & assumption flags (Standard+)",
    category: "money",
    summary: "See how your margins, labor, rent, marketing, net margin and growth compare to vetted industry ranges — with out-of-norm assumptions flagged, plus a one-page snapshot PDF for your pitch or lender package.",
    body: `# Industry benchmarks & assumption flags

Once your Business Plan is complete, the **Industry benchmarks** section gives you two kinds of comparison.

---

## 1 · Industry standard ratios (curated)

Unlike the cohort comparison (which needs other Three Little Birds founders in your industry), the **Industry standard ratios** panel surfaces *immediately* — it ships a vetted reference dataset. For your industry it shows the typical 25th–75th-percentile range for:

- **Gross margin** and **Cost of goods & services**
- **Labor & payroll**, **Rent & occupancy**, **Marketing & advertising** (each as a % of revenue)
- **Net profit margin**
- **Annual revenue growth**

Your own figures — derived automatically from your **budget line items** and **revenue projection** — appear next to each range with an In range / Above / Below indicator.

## 2 · Assumption flags (with one-tap fixes)

When one of your numbers sits outside the typical range on the unfavorable side (e.g. labor far above the norm, or net margin below it), it's called out in an amber **assumptions** box so you know exactly what a lender or investor might question — before they do. Each flag carries a **"Fix this"** deep-link that scrolls you straight to the exact **budget** or **revenue** line driving it and gives it a brief highlight pulse, so you go from "this looks off" to editing the right number in one tap.

## 3 · Tag your budget lines (so the rollup is yours)

The benchmarks group your spending into categories — labor, rent, marketing, and so on. We auto-detect each line's category from its name, but you can **override it explicitly**: every budget line has a benchmark-category dropdown. The same explicit tag also flows into the Driver-based forecast's operating expenses when you sync. Whenever you retag a line, the **Industry benchmarks panel auto-refreshes** so your comparison always reflects how *you* classify your costs.

## 4 · "You vs. your industry" snapshot PDF

Tap **Snapshot PDF** in the panel header to download a one-page, brand-styled sheet comparing your business to typical {industry} benchmarks — perfect to drop into a pitch deck or alongside your lender package.

The same benchmark page is also **auto-included** in your exports: it's appended to the **Lender package PDF** (a credibility page for your banker) and added as a **slide in the Pitch deck** (so investors see how you compare) — no extra steps.

## Where to find it

- Open any **complete** Business Plan → **Industry benchmarks** section in the left rail. (The section unlocks once the plan is at 12/12.)
- Tier: **Standard or Advanced**.
- Note: reference ranges are curated from public small-business sources (SBA, BLS, RMA, industry norms) and are directional, not guarantees.`,
  },
  {
    slug: "whatif-scenario-planner",
    title: "What-if scenario planner (Advanced)",
    category: "money",
    summary: "Model alternate forecasts by changing revenue, growth, cost of goods and monthly spend — and compare the 3-year outcome side-by-side against your base plan.",
    body: `# What-if scenario planner

The **What-if planner** (an **Advanced** feature) lets you stress-test your forecast without touching your real plan. Open it from the plan's **Plan-actions** menu → *What-if planner*, or go to **/business/{id}/scenarios**.

---

## The four levers

Create a named scenario and adjust any of:

- **Year-1 revenue**
- **Annual growth %**
- **Cost of goods & services %**
- **Monthly operating spend**

Defaults are pulled from your existing plan so you start from reality.

## What you get back

Every scenario computes a **deterministic** (no-Three Little Birds, formula-driven) 3-year outcome:

- 3-year P&L (revenue, COGS, gross profit, operating expenses, net income per year)
- **Year-3 net income** and **3-year cumulative profit**
- **Break-even revenue** and the **year you turn profitable**
- A **delta vs. your base plan** so the trade-off is obvious at a glance

## Compare side-by-side

The comparison table puts your **Base plan** row at the top and every saved scenario beneath it. Save up to **10** scenarios per plan; edit or delete any time.

## Where to find it

- Per plan: **Plan-actions** menu → *What-if planner*, or **/business/{id}/scenarios**.
- Tier: **Advanced**.`,
  },
  {
    slug: "driver-forecasting",
    title: "Driver-based forecast (Advanced)",
    category: "money",
    summary: "Build a bottom-up, 3-year monthly P&L from your real drivers — revenue streams, hires and operating expenses — sync expenses from your Budget in one click, and export a 36-month CSV.",
    body: `# Driver-based forecast

Most quick forecasts start with a single top-line number and grow it. The **Driver-based forecast** (an **Advanced** feature) does the opposite: it builds a bottom-up, **3-year monthly P&L** from the actual drivers of your business, so the numbers come from decisions you can defend.

Open it from the plan's **Plan-actions** menu → *Driver forecast*, or go to **/business/{id}/forecast**.

---

## The three driver sections

- **Revenue streams** — model each line as units × price with a monthly growth rate. Add as many streams as your business has.
- **Personnel** — list roles with headcount and a start month; the engine turns them into monthly payroll.
- **Operating expenses** — your recurring monthly costs (rent, software, marketing, …), each carrying a benchmark category.

As you edit, a live **chart**, a monthly **P&L table** (revenue, COGS, gross profit, payroll, operating expenses, net income, cumulative) and **KPI tiles** update in place.

## Sync operating expenses from your Budget

Hit **Sync from Budget** to pull your operating expenses straight from the **Budget & burn** module — no re-typing. Each line keeps its benchmark category so it rolls up consistently with the Industry benchmarks. If you later change the Budget, the forecast flags that it has **drifted since your last sync** so you can re-sync with one click and stay in step.

## Export & downstream uses

- **Export a 36-month CSV** of the full monthly P&L for your own spreadsheets or your accountant.
- Your **what-if scenarios** layer directly on top of this forecast when it exists.
- The forecast also feeds your **Lender package** and **Pitch deck** so every export tells one consistent story.

## Where to find it

- Per plan: **Plan-actions** menu → *Driver forecast*, or **/business/{id}/forecast**.
- Tier: **Advanced**.`,
  },
  {
    slug: "pitch-deck-presets",
    title: "Pitch deck presets & customization (Standard+)",
    category: "plan",
    summary: "Reorder and toggle slides, then save the layout — or load a built-in Investor or Banker preset to start from the right story instantly.",
    body: `# Pitch deck presets & customization

Your investor-ready **Pitch deck** PDF is fully customizable, so the same plan can speak to two very different audiences. Open it from the plan's **Plan-actions** menu → *Customize deck*.

---

## Reorder & toggle slides

- **Reorder** slides by dragging, or with the up/down arrows.
- **Toggle** any slide on or off for a given download.
- Your layout is **saved per plan** and applied every time you export.
- The **cover slide always comes first**, and data-dependent slides (like the live CRM pipeline) only appear when there's data to show.

## Built-in presets

Load a starting layout with one click:

- **Investor** — a story-led order that leads with the problem, market, traction and the ask.
- **Banker** — a financials-forward order that puts the numbers a lender cares about up front.

Apply a preset, then tweak from there — nothing is locked.

## Save your own presets

Happy with a layout? **Save it as a named preset** and reuse it across any of your plans. Your custom presets are saved to your account, so a layout you perfected for one venture is one click away on the next.

## Where to find it

- Per plan: **Plan-actions** menu → *Customize deck*.
- Tier: **Standard or Advanced** (Pitch deck export tier).`,
  },
  {
    slug: "sample-plan-library",
    title: "Sample plans — clone a proven starter (all plans)",
    category: "getting-started",
    summary: "Browse polished example business plans across industries and clone one to your workspace as a fully-editable starting point — no blank page.",
    body: `# Sample plan library

Staring at a blank wizard is the hardest part of starting. The **Sample plans** library gives you a running start with polished, complete example plans across industries.

---

## Browse

Open **Examples** in the top nav (or the **Browse example plans** button on an empty dashboard) to see sample plans for a coffee shop, e-commerce brand, SaaS, tutoring service, salon, electrical contractor, boutique inn, physical-therapy clinic, real-estate brokerage and more.

## Preview

Click **Preview** on any card to study its executive summary, 3-year revenue projection, customer-acquisition plan and launch roadmap before you commit.

## Clone & make it yours

Click **Use this** (or **Clone to my workspace** in the preview) and a fresh, **fully-editable** copy lands in your account named "<plan> (my copy)". Because the samples are complete plans, the cloned copy arrives with its Health Check and Industry Benchmarks already unlocked — a perfect scaffold to tailor to your own venture.

## Where to find it

- Top nav: **Examples** (**/sample-plans**), or the dashboard empty-state button.
- Tier: **available on every tier** (cloning respects your plan limit).`,
  },
  {
    slug: "whats-new-jun-2026",
    title: "What's new (June 2026)",
    category: "getting-started",
    summary: "Industry benchmarks with assumption flags + a snapshot PDF, a what-if scenario planner, and a sample-plan library you can clone.",
    body: `# What's new — June 2026

Three features that help you build faster and pitch stronger.

---

## 1 · Industry benchmarks + assumption flags + snapshot PDF (Standard+)

The **Industry benchmarks** section now includes a curated **Industry standard ratios** panel — typical ranges for gross margin, COGS, labor, rent, marketing, net margin and growth in your industry — with your own figures compared against each range. Anything outside the norm is **flagged** as an assumption to revisit. Tap **Snapshot PDF** for a one-page "your business vs. your industry" sheet to drop into a pitch or lender package. See *Industry benchmarks & assumption flags*.

## 2 · What-if scenario planner (Advanced)

Model alternate forecasts without touching your real plan. Change Year-1 revenue, growth %, cost of goods % and monthly spend, and compare the **3-year outcome side-by-side** against your base plan — Year-3 net, cumulative profit, break-even and the year you turn profitable. Open **Plan-actions → What-if planner**. See *What-if scenario planner*.

## 3 · Sample plan library (all tiers)

Don't start from a blank page. Browse polished example plans across industries, preview their strategy and financials, and **clone** one to your workspace in a click. Find it under **Examples** in the top nav. See *Sample plans — clone a proven starter*.`,
  },
  {
    slug: "interactive-reports-dashboard",
    title: "Interactive reports dashboard (Standard+)",
    category: "money",
    summary: "Twelve visual, filterable reports for your business — budget, pipeline, funding, cash flow, unit economics, break-even and more — plus a one-page Investor snapshot you can print to PDF.",
    body: `# Interactive reports dashboard

Standard and Advanced plans turn your numbers into charts you can explore. Open it from **Plan actions → Reports** on any plan (or /business/{id}/reports).

---

## The report menu (collapsible sidebar)

A left sidebar lists every report. Click one to switch; tap **Collapse** to shrink it to icons and give the charts more room. The twelve reports are:

- **Budget** — startup-cost breakdown and monthly burn vs revenue.
- **Pipeline** — your sales deals by stage and value, weighted by win-probability.
- **Funding** — your capital stack (savings, investors, grants, loans, won sales).
- **Cash flow** — a month-by-month waterfall with a "runs out of cash" marker.
- **Unit economics** — price, cost, margin and CAC payback for a single sale.
- **Break-even** — how many units or months until revenue covers costs.
- **Revenue bridge** — what drives revenue from Year 1 to Year 2.
- **Market size** — your TAM to SAM to SOM funnel.
- **Scenarios** — compare your saved pricing scenarios side by side.
- **Timeline** — your launch roadmap by phase and status.
- **Sensitivity** — which assumptions swing your outcome the most.
- **Investor snapshot** — a one-page summary of your headline numbers.

## Guidance on every report

Each report opens with a short, plain-English note explaining what it shows and how to read it. Once you know your way around, tap the X to dismiss it — your choice is remembered per report.

## Filters (Pipeline)

The filter bar — date range, product/service, customer segment and stage — drives the **Pipeline** report so you can slice your deals. It appears only while the Pipeline report is selected, so the other reports stay uncluttered.

## Print the Investor snapshot as a one-pager

The **Investor snapshot** report has a **Print / save PDF** button. It prints only that report — no app navigation, sidebar or filters — as a clean A4 one-page packet with your 3-year revenue, capital raised, monthly burn, runway, a revenue-projection chart, a capital-mix chart and your break-even line. Perfect to hand to a banker or investor.

- Where: **Plan actions → Reports** (or /business/{id}/reports)
- Tier: **Standard and Advanced**`,
  },
  {
    slug: "whats-new-late-jun-2026",
    title: "What's new (late June 2026)",
    category: "getting-started",
    summary: "A brand-new interactive reports dashboard with a printable investor one-pager, one-click 'restore best' pricing, a competitive-landscape table view, and tidier investor scenarios & activity.",
    body: `# What's new — late June 2026

The latest round of upgrades, focused on seeing your numbers clearly and moving faster.

---

## 1 · Interactive reports dashboard (Standard+)

Every plan now has a **Reports dashboard** with twelve visual, filterable reports — budget, pipeline, funding, cash flow, unit economics, break-even, revenue bridge, market size, scenarios, timeline, sensitivity, and a one-page **Investor snapshot**. A collapsible sidebar switches between them, each opens with a short plain-English guide, and the Pipeline filters only show when you need them. Open it from **Plan actions → Reports**. See *Interactive reports dashboard*.

## 2 · Print the Investor snapshot to PDF

The Investor snapshot report prints as a clean **one-page PDF** — just your headline numbers and key charts, no app chrome — ready to hand to a banker or investor. Tap **Print / save PDF** on the report.

## 3 · One-click "Restore best" pricing

On the Products & Services pricing model, the Saved-scenarios bar now has a **star Restore best** button that instantly applies your highest-3-year-revenue saved scenario — no hunting for which strategy won.

## 4 · Competitive landscape: table view

Three Little Birds competitive-landscape grid now has a **Cards / Table toggle**. The table shows Competitor, Strengths, Weaknesses, Our edge and an Add-to-tracker action per row. Three Little Birds-found competitors are auto-added to your Deep competitor briefs, and any tracked competitor's button flips to a green "Added".

## 5 · Tidier investor scenarios & activity

Investor scenarios are now capped at **3 per plan** to keep your comparison focused, and the Dashboard's **Recent activity** widget clears an item when you act on it — plus a **Clear all** button to reset the list in one tap.`,
  },
  {
    slug: "whats-new-feb-2026",
    title: "What's new (February 2026)",
    category: "getting-started",
    summary: "Personal investment input, smarter Infrastructure regen, faster login routing, Investor Preview, white-label custom domains.",
    body: `# What's new — February 2026

A round-up of the changes that landed this month. Each item links to the page or article that explains it in full.

---

## 1 · "How much are you investing?" on the wizard

Step 4 of the wizard now captures your **personal capital contribution** alongside Y1 revenue. It's the cash you're putting in yourself — savings, personal loans, family contributions — and Three Little Birds uses it to:

- Size **runway** correctly on the Plan Health Check + dashboard.
- Tailor the **Infrastructure** section (leaner ops for self-funded, broader recommendations when there's outside capital).
- Anchor the **Budget** module's burn / startup costs draft.

Leave it as 0 if you haven't decided yet — the field is optional and you can edit it later via the wizard or the Edit-questionnaire flow.

The Self-funded runway calculator (Funding Strategy section of your plan) also picks this value up automatically — Starting savings is pre-filled with your answer the first time the panel renders, so the runway number on the dashboard is grounded in your real intent instead of a $10K placeholder. Monthly burn is seeded the same way: once you've populated your Budget · monthly burn rows, the calculator opens with the sum of those rows (actual_amount when you've logged actuals, planned otherwise) so the panel matches the dashboard from the start. Monthly revenue closes the loop — once you've logged any annual revenue actuals on the 3-Year Revenue Projection, the calculator divides the most-recent year by 12 and pre-fills the field so the runway reflects your live trading reality, not a planning sandbox.

## A · Runway tile on the Dashboard

That same runway number now lives on every business card on your Dashboard, next to Plan Health and Compliance Risk. The colour band reads at a glance — green when you're ≥ 6 months, amber 3–6, red < 3 — and the value links straight to the Funding Strategy section so you can adjust inputs without hunting through the plan. Brand-new plans (Business Plan not yet at 12/12) show a "RUNWAY · finish plan" lock identical to the Health Check tile, then unlock the moment you complete the flow.

## B · Monthly Revenue integrated into the Budget table

The **Monthly check-in** inside the Budget section now folds Revenue rows in alongside Expense rows — one unified table per month with a TYPE pill on every row (grey "Expense" vs green "Revenue"). Founders log the entire month — what came in *and* what went out — in a single Save action that dispatches both PATCHes in parallel.

Set up your revenue sources once at the top of the Budget section (Subscriptions, Retainers, Bakery Sales…) and they appear inline in every month's check-in. Variance colour semantics invert for revenue: over-plan is green, under is red — the opposite of how expense rows read. A **Net P&L this month** row at the bottom of the table sums revenue actuals minus expense actuals so you can see whether the month was profitable at a glance.

**Runway picks the freshest signal**: the latest month with revenue data wins outright. When no per-month actuals exist yet, runway falls back to the average of your template planned sum and your 3-Year Revenue Projection annual actuals ÷ 12. When neither, the runway tile defers to your savings ÷ net burn alone.

If you've also logged annual revenue actuals on the 3-Year Revenue Projection grid, the template editor exposes a one-click "Pre-fill from calculated" button so you don't have to retype the number.

## C · Receipt scan → invoice scan (income mode)

The existing **Scan receipt** dialog now scans **invoices too**. When you have revenue sources set up, the dialog grows a small toggle on its confirmation step:

- **Expense** (default) — money the business spent. OCR maps line items to monthly-burn categories. Same behaviour as before.
- **Income** — money the business received. OCR pivots to *payer* / *invoice-line* semantics and maps amounts to your revenue sources.

One flow, two semantic targets. The button on the Monthly check-in re-labels itself to "Scan receipt/invoice" the moment a revenue template exists, and the Apply step writes to the right side of the ledger automatically.

## D · Grants mini-widget on the Dashboard

Every business card on the Dashboard with at least one saved grant now shows a compact **Grants mini-widget** alongside the Health Check / Compliance / Runway tiles. Reads the business' grants list directly so it's instant — no extra API call.

Status chips render only for non-zero buckets to keep the widget tight: *"2 saved · 1 drafting · 1 submitted · 1 awarded"*. **Awarded** grants get a green pulsing trophy icon so wins stay celebrated. Clicking the widget deep-links to the per-plan Grants Board (the same view that's now scoped to the selected business since the recent Board update).

## E · Revenue trend chart in the Budget section

A new emerald-coloured mini-chart sits at the bottom of the Budget section, sparkline-style: last 6 months of revenue actuals as bars + line, with a dashed reference line marking your template-planned sum. Bars taller than the line = over-plan (good for revenue, opposite of expense).

The header strip shows the most-recent month's variance vs plan (e.g. *"May +$500 vs plan"*). Renders nothing until you've logged at least one month of revenue actuals so brand-new plans don't show a dead chart.

## 2 · Smarter Infrastructure regeneration

Infrastructure (Personnel · Technology · Administration) regenerates as **one combined unit** — a single button refreshes all three columns coherently.

It's now capped at **one regeneration per business**, far stricter than the 3-per-section cap on other regenerable sections. The reason: Infrastructure feeds straight into your Budget, so we don't want it to drift out of sync via repeated rerolls.

**Need another pass?** Open the wizard, edit your Description, and save. Changing the Description automatically resets the Infrastructure regen counter — because the new framing is what should drive the new staffing / tools list. Other section caps stay where they were.

## 3 · Faster login routing for returning founders

If you've already completed (or skipped) your **Personality Profile**, login (email/password or Google) now sends you **straight to /dashboard**. New founders or anyone with an unsaved profile drop into /profile first so the questionnaire runs once.

This replaces the old "land on /dashboard, then bounce to /profile" flash for incomplete profiles.

## 4 · Investor Preview toggle (on the founder's plan view)

Add the query parameter ?investorPreview=1 to any plan URL — or click the **"What investors see"** link on the public-share panel — to flip your Business Plan into investor-preview mode without leaving the page. The investor framing renders inline (executive summary, opportunity, pricing model, traction stats) so you can sanity-check exactly what a marketplace match will see before publishing.

## 5 · B2B Advisor custom domain whitelabel

Advisors can now connect a custom subdomain (e.g. plan.youragency.com) to their workspace. The Custom Domain panel walks you through the CNAME + TXT records, polls live DNS for propagation status, and flips the co-branded login page over the moment validation passes.

## 6 · Business Plan side panel inside the Investor Marketplace

The plan-section side rail now appears inside the Investor Marketplace view too, so founders and investors can navigate between Executive Summary, Pricing, Traction, etc. without backing out to the dashboard.

---

## Small but useful

- "Re-generate full plan" was removed — per-section regen is the only path now, so manual edits never get clobbered.
- Investor pages auto-resync your Pricing Model whenever you edit Products & Services.
- The "Mark Done" button on Weekly Focus + Deep Competitor briefs are now **Standard+ tier features** (matching the Products & Services research gate).`,
  },
  {
    slug: "whats-new-late-feb-2026",
    title: "What's new (late February 2026)",
    category: "getting-started",
    summary: "Health-hero smart-resume deep-links + hover-preview sparklines, a slimmer Dashboard tile, the Best strategy on the Funding tile, and a broader initial plan auto-generation sweep.",
    body: `# What's new — late February 2026

A round of quality-of-life upgrades focused on the Business Hub, the Dashboard tile, and how much your first plan draft comes back with.

---

## 1 · Health hero pills now smart-resume

Every leg pill on every Health hero (Plan · Launch · Business · Operating · Funding · Marketing) now **deep-links back to the last section you were editing** for that leg — not just a generic top-of-page anchor. Click a Plan pill from the Hub and you land inside your business plan on the exact section (Pricing, Customer Acquisition, Revenue Projection…) that you were last in. Click Runway from Financial Health and you jump straight to the Budget row that moves it.

The score is never a dead-end and the surface never forgets where you left off.

## 2 · Hover-preview chips with mini-sparklines

Hover any Health hero leg pill and a compact chip pops up with:

- A **7-day mini-sparkline** so you can see the trend without clicking through.
- The **week-over-week delta** — green ▲ up, red ▼ down, grey — flat/fresh.
- The last three numbers behind the sparkline so the story is on the pill, not two clicks away.

The chip is absolutely positioned so it never distorts the pill row or wraps oddly on mobile. Purely additive — the click behaviour is unchanged.

## 3 · A slimmer Dashboard tile for operating plans

Once a plan flips to Operate, the Dashboard tile now shows *only* the things that matter for a live business:

- **Removed** from operating plans: Compliance chip, Next task chip, Runway tile, Capital Stack, Launch band, Grants mini-widget, the "Plan Health" and generic "Health" pills.
- **Kept** on operating plans: name, Operating Health hero, revenue trend, and the primary action row.
- **Moved:** the *Best* pricing / funding strategy shortcut lives on the Hub's **Funding tile** (not the Dashboard tile) — one home for money moves.
- **Moved:** the plan delete icon now sits on the same row as **Activate** so the tile keeps a single control strip.

Pre-launch plans (still in Plan or Launch phase) keep the fuller widget stack — this trim only applies once you flip to Operate.

## 4 · Help removed from the top-nav menu

The Help / KB surface is still at **/help** (linked from the footer and inside the Business Hub), but it no longer clutters the desktop + mobile navigation. This keeps the primary nav focused on the four verbs founders actually navigate to: Dashboard, Plan, Roadmap, Hub.

## 5 · Broader initial plan auto-generation

The moment you submit the wizard, the app now fires a background sweep that auto-drafts **six extra sections** on top of the standard plan essentials — without you having to click Regenerate on each one:

- **Competition** — competitive analysis + deep briefs seed
- **Market Analysis** — TAM/SAM/SOM sizing (from your industry + geography)
- **Goals** — first draft of business goals aligned to your Y1 target
- **Infrastructure** — Personnel · Technology · Administration
- **Customer Acquisition** — channel-by-channel plan
- **Compliance** — jurisdiction-aware checklist for your city / county / state / industry

The sweep runs in parallel via **asyncio.gather**, skips any section you've already touched (so nothing overwrites your edits), and swallows per-section failures independently — so a hiccup on one section can't stall the others. Result: your first draft comes back **substantially more filled-in** than before, and the Dashboard's launch-readiness signals go green faster on day one.

---

## Small but useful

- The Dashboard's plan tile control row is more consistent — a single Activate + Delete strip on every operating plan card.
- Hover-preview chips use absolute positioning with a fixed z-index above the score header so they never mis-align, wrap, or fight the surrounding grid on Safari.
- The Health-scores KB article's Plan Health table now matches the shipping formula: **4 components × 25 pts each** (Customer acquisition was removed Feb 17 · 2026).`,
  },
  {
    slug: "strategic-plan-hierarchical-view",
    title: "Strategic Plan · Hierarchical View (Operating + Pre-Launch)",
    category: "getting-started",
    summary: "The live SVG chart of Business Drivers → Strategic Goals → Projects → Roadmap Tasks (Operating), or Business Plan → Compliance → Health → Goals (Pre-Launch), with draggable Mini-Map, health-coloured edges, and a per-user default in Settings > Workspace.",
    body: `# Strategic Plan · Hierarchical View

Every plan gets a **live SVG chart** of itself on a single screen — Business Drivers, Strategic Goals, Projects and Roadmap Tasks (Operating Mode), or Business Plan sections, Compliance, Health legs and Goals (Launching Mode / Pre-Launch). Edges are colour-coded by health so a slipping goal or an at-risk milestone jumps off the screen, and every node is a click-through into the source surface.

---

## 1 · Where it lives

**Operating Mode plans** — The moment a plan graduates from Launching → Operating, the "Business Plan" surface renames to **Strategic Plan**. The default view for Strategic Plan is the hierarchical **Overview** chart (Business Drivers → Strategic Goals → Projects → Roadmap Tasks).

**Launching Mode plans** — The classic sectioned Business Plan is still the default, but a **Pre-Launch Overview** tab shows the same style of chart across your Business Plan sections, Compliance items, Health legs and Goals.

You can flip the default per-user in **Settings > Workspace > Default Strategic Plan view / Default Business Plan view** so the map opens first for power users, or the classic sections stay first for founders who prefer the linear reading view.

## 2 · How to read the chart

- **Nodes** are the objects — Drivers, Goals, Projects, Tasks, Sections, Compliance items, Health legs.
- **Edges** connect parents to children — coloured **green** if the child is healthy, **amber** if drifting, **red** if red-line.
- **Overflow indicators** appear when a parent has more children than fit in its column — click through to see the full list on the source surface.
- **Fit-to-width vs actual-size** — a per-plan toggle in the top-right, persisted in localStorage so your zoom preference sticks.

## 3 · The Mini-Map

On tall or wide plans, a small bird's-eye Mini-Map anchors to the corner of the chart. Drag the viewport rectangle to scrub in both axes — horizontally to jump between Drivers, vertically to walk down deep goal trees. The Mini-Map lives on both Operating (Strategic Plan) and Launching (Pre-Launch) Overviews.

## 4 · Click-through actions

- **Click a Strategic Goal card** → opens the goal editor with its 4-week revenue sparkline, attributed deals, attributed campaigns, and a Find Agents shortcut.
- **Click a Project card** → drops into the project's own mini-roadmap (tasks with status, owners, due dates, progress ring).
- **Click a Roadmap Task node** → opens the task in place with all its metadata + shortcuts (Send to CRM / Campaigns, attribution chip if any).
- **Click a Business Plan section / Compliance item / Health leg** → same smart-resume as the Health hero pills — you land inside the source surface at the exact spot you were last editing.

## 5 · Setting the default view

Head to **Settings > Workspace > Default Strategic Plan view** and pick:

- **Overview (default)** — the hierarchical SVG chart opens first.
- **Traditional (sections)** — the classic sectioned Business Plan opens first.

Set the equivalent for launching-mode plans under **Default Business Plan view**. Individual per-plan choices always win — this is just the fallback for plans you haven't customised yet.

## 6 · What powers it

- The chart is a **pure client-side SVG** with custom layout — no external chart library, no runtime cost on the backend.
- Node positions are computed from the plan graph on every render so adding a Goal or Project shows up immediately without a re-fetch.
- Health colours read from the same live signals that drive the Health hero pills — so the chart and the pills can never disagree.`,
  },
  {
    slug: "attribution-loop",
    title: "Attribution Loop — Goals · Projects · Deals · Campaigns",
    category: "getting-started",
    summary: "Every closed-won deal and every marketing campaign carries an origin_ref back to the Strategic Goal or Roadmap Task that sparked it — powering 4-week revenue sparklines on Goal cards, one-click Deal/Campaign filters, prefilled draft creation, and task-level attribution chips on the Kanban.",
    body: `# Attribution Loop — from Goal to Revenue

The Attribution Loop closes the gap between **what you planned** (Strategic Goals + Roadmap Tasks) and **what actually happened** (closed-won CRM deals + marketing campaigns). Every deal and campaign carries a hidden \`origin_ref\` pointing back at the Goal or Task that sparked it, powering revenue rollups, filters and sparklines across the app.

---

## 1 · How the link gets made

Three ways an \`origin_ref\` gets set on a Deal or Campaign:

- **Prefill From Origin** — click the "Add to CRM" or "Add to Campaigns" shortcut chip on any Strategic Goal or Roadmap Task. The new-deal / new-campaign dialog opens with the title prefilled AND the origin link already stamped on the record. Save, and the loop is closed.
- **Manual pick** — inside the Deal or Campaign form, use the "Origin" picker to link a Goal or Task after the fact.
- **URL params** — links like \`/business/:id/crm?new=1&origin_type=goal&origin_id=<goal-id>\` open the dialog prefilled and pre-stamped. Handy when you're sharing a plan surface across advisors or agents.

## 2 · Where the loop shows up

- **Goal card · 4-week revenue sparkline** — every Strategic Goal card now shows a mini bar chart of the last four weeks of closed-won revenue attributed to it. Hover for the exact number, click for the drill-down.
- **Goal Attribution block** — inside the Goal edit dialog: total pipeline, total won, deal count, campaign count, plus the same 4-week sparkline.
- **CRM pipeline · Attribution Filter Bar** — hitting the CRM with \`?origin_goal=<goal-id>\` filters the pipeline to only deals attributed to that goal. A pill at the top of the page shows the active filter and the "clear" button restores the full pipeline.
- **Campaigns · Attribution Filter Bar** — the same filter bar works on the Campaigns list.
- **Kanban attribution chip** — Roadmap tasks that have any attributed deals or campaigns show a small chip on the KanbanCard; click it to open the attributed records filtered on that task.

## 3 · CRM-tracked Goals

Two goal kinds — **Revenue** and **Customers** — can flip a "Track from CRM" toggle. When ON:

- \`progress\` is auto-computed from your CRM pipeline via the CRM rollup.
- **Revenue goals** sum closed-won deal values.
- **Customers goals** count closed-won deals (unique customers).
- The goal card grows a small purple "CRM" badge and hides the manual "Update progress" input.
- Under the progress bar you see a projected pipeline hint (\`+$X projected\` on revenue, \`N open deals\` on customer goals) — the *weighted* open pipeline, shown as a preview but not counted.

When OFF, progress stays manual — you type your latest number.

## 4 · Data model

Two document collections gained a new field:

- \`crm_deals.origin_ref: { type: "goal" | "task", id: str }\` — validated on POST/PATCH.
- \`campaigns.origin_ref: { type: "goal" | "task", id: str }\` — same shape.

The backend endpoint \`GET /api/businesses/{id}/goals/{goal_id}/attribution\` returns:

- \`totals\` — pipeline, won, deal count, campaign count.
- \`deal_status_counts\` and \`campaign_status_counts\` — per-status buckets.
- \`weekly_won\` — 4-week rolling revenue bins (feeds the sparkline).

Task-level attribution rolls up to the parent Goal so a goal card reflects both its own directly-attributed revenue AND the revenue from its child tasks.

## 5 · Why it matters

Before the Attribution Loop, "how much revenue did this Goal actually produce?" required a spreadsheet. Now every Goal card answers it live — and so does every Task, every Campaign, every filter pill. When a Goal is under-performing, the sparkline shows it. When a Campaign is crushing it, you can trace which Goal is benefiting. The plan and the pipeline are finally the same object.`,
  },
  {
    slug: "whats-new-feb-2027",
    title: "What's new (February 2027)",
    category: "getting-started",
    summary: "Strategic Plan hierarchical Overview + Mini-Map, Attribution Loop across Goals · Projects · Deals · Campaigns, Projects rename + drag-and-drop kanban, Settings restructured into 6 tabs, Send Wallet moved into Settings, SMS Unsubscribes moved into Settings.",
    body: `# What's new — February 2027

A big month across the plan surfaces, the messaging + billing hub, and the growth board.

---

## 1 · Strategic Plan · Hierarchical Overview (Operating + Pre-Launch)

Every plan now has a **live SVG Overview** — Business Drivers → Strategic Goals → Projects → Roadmap Tasks on operating plans, or Business Plan sections → Compliance → Health → Goals on launching plans (Pre-Launch Overview).

- Edges are **health-coloured** — green healthy, amber drifting, red red-line — so slipping items jump off the screen.
- A **draggable Mini-Map** in the corner lets you scrub around big plans (both axes).
- **Overflow indicators** warn when a Goal has more Projects than fit.
- **Save Overview Zoom** — the fit-to-width vs actual-size toggle persists per plan in localStorage.
- Default view (Overview vs. Traditional sections) is a per-user preference under **Settings > Workspace**.

See the [Strategic Plan · Hierarchical View](/help?tab=kb&article=strategic-plan-hierarchical-view) article for the full walkthrough.

## 2 · Attribution Loop — Goals · Projects · Deals · Campaigns

Every closed-won deal and marketing campaign now carries an \`origin_ref\` back to the Strategic Goal or Roadmap Task that sparked it.

- **4-week revenue sparkline** on every Goal card.
- **Attribution Filter Bar** on CRM + Campaigns pages — one click filters the pipeline to a specific Goal.
- **Prefill From Origin** — click "Add to CRM" or "Add to Campaigns" from a Goal / Task and the new-record dialog opens prefilled + pre-stamped.
- **Kanban task attribution chip** — Roadmap tasks with attributed revenue show a chip that deep-links to the filtered pipeline.
- **CRM-tracked Goals** — Revenue + Customers goals auto-compute progress from closed-won deals.

Full details in the [Attribution Loop](/help?tab=kb&article=attribution-loop) article.

## 3 · Experiments renamed to Projects · with drag-and-drop kanban

The Experiments board is now the **Projects** board on every operating plan. Same four columns (Ideas · Running · Won · Lost), same templates library, plus:

- **Native drag-and-drop** between columns — drop a card into Won and confetti fires + the linked Goal's progress nudges.
- Cards are **grab-cursor** while draggable, dim to 40% opacity while airborne, and the drop target lights up in purple so you always see where the card will land.
- The **"Send Project to Roadmap"** button is removed — Projects now live entirely in their own mini-roadmap (ProjectTasksPanel) so double-writing tasks into the top-level launch roadmap no longer muddles ownership.
- Projects are **hidden on Pre-Launch plans** (they're an operating-mode surface) — no more phantom upgrade lock in the Hub Tools grid for launching founders.
- Available on **Advanced** subscription.

## 4 · Settings restructured into 6 tabs

The Settings page went from one long scroll into **6 focused tabs** with per-tab URL sync (\`?tab=brand\`, \`?tab=wallet\`, etc.):

- **Assistant** — Three Little Birds Voice tone, Guided Mode, Custom Instructions.
- **Brand** — Brand Kit, logo generator, signature multi-crop, sepia retune.
- **Messaging** — Email Preferences, Send Budgets, Segment Budgets, SMS Unsubscribes.
- **Wallet** — Send credits balance, order history, activity ledger, auto-buy rule (moved from \`/settings/credits\`).
- **Workspace** — Hub Preferences, integrations, default plan / strategic view.
- **Account** — Password.

Bookmarkable — share a link like \`/settings?tab=brand\` and it lands directly on the Brand tab.

## 5 · SMS Unsubscribes moved from CRM → Settings

The SMS Unsubscribes tile (STOP reply counter + Twilio inbound webhook setup docs) is no longer on the CRM screen. It now lives in **Settings > Messaging > SMS Unsubscribes** alongside the other list-health surfaces. Multi-plan founders get a plan picker; single-plan founders auto-see their only plan.

## 6 · Term of the Week above the Journey ribbon

Small tweak, big vibe — the Term of the Week glossary tile moved from the bottom of the Hub to right above the Journey ribbon. Now the passive vocabulary nudge is one of the first things you see when opening a plan.

## 7 · ProjectPage Kanban tab renamed to "Board"

Inside a Project, the first tab (default view) is now labelled **Board** to match the top-level Roadmap. Internal keys preserved so existing deep-links keep working.

---

## Small but useful

- \`/settings/credits\` redirects to \`/settings?tab=wallet\` — old bookmarks keep working.
- \`orderedFinancialTools\` on the Hub now respects a \`hidden\` flag so tools contextually inapplicable to a plan (like Projects on a pre-launch plan) get filtered out entirely instead of showing a misleading upgrade lock.
- Anchor jest tests updated to point at the extracted section components (\`BrandKitSection\`, \`HubPreferencesSection\`, \`SmsUnsubscribesSection\`, \`SendWalletSection\`) so future refactors don't drift.`,
  },
  {
    slug: "your-first-plan",
    title: "Your first plan in 15 minutes",
    category: "getting-started",
    summary: "From the wizard to a complete plan + roadmap, step by step.",
    body: `# Your first plan in 15 minutes

The wizard captures the eight things any plan needs:

1. **Business name** — a working title is fine, change it later
2. **Description** — 1-2 sentences in plain English
3. **Offering** — Service, Product, or Both
4. **Industry** — pick the closest match
5. **Target market** — age range, geography, who specifically
6. **Timeframe** — when do you want to launch? (3 / 6 / 12 / 24 months)
7. **Y1 revenue target** — your honest first-year revenue ambition
8. **Personal investment** — how much of your own capital is going in (savings, personal loans, family contributions). Three Little Birds uses this to size runway and tailor infrastructure recommendations.

After Submit, you'll see two parallel "Drafting…" progress bars — one for the Plan, one for the Roadmap. Both finish in roughly 30-60 seconds.

## What you get
- Refined business description
- Competitive analysis (3-5 competitors, gaps you can win)
- Pricing model with rationale
- 3-year revenue projection
- Customer acquisition plan
- Infrastructure & operations plan
- 12-16 phased roadmap tasks across To Do → In Progress → Done

You can regenerate any section independently if it doesn't fit your vision.`,
  },
  {
    slug: "step-by-step-tutorial",
    title: "Step-by-step: create and understand your Business Plan",
    category: "getting-started",
    summary: "A guided tour through every wizard input, every plan section, and how to read the output.",
    body: `# Step-by-step: create and understand your Business Plan

This tutorial walks you all the way from the empty dashboard to a Business Plan you can defend in front of an investor. Aim for 25-40 minutes the first time, then 10 minutes per iteration after that.

---

## Part 1 — Before you start (2 min)

Open the **Personality Profile** wizard from your avatar menu first. Three Little Birds uses your age range, business experience, strengths, and concerns to tailor every section. A founder marked "first-time, risk-averse, strong on operations" will get a noticeably different plan than "serial founder, growth-aggressive". You can skip it, but skipping costs you fit. You can update it any time and regenerate sections to re-tune the voice.

If you have a particular Three Little Birds personality preference (formal · friendly · concise · bold) set it under **Settings → Three Little Birds Voice** before generating. Plan copy adapts in real time on regeneration.

---

## Part 2 — The Wizard (5-7 min)

Click **+ New Business** on the dashboard. Five steps:

**Step 1 — Basics.** Pick a working name and a 1-2 sentence description in plain English. Don't optimize for buzzwords ("Three Little Birds-powered platform that disrupts…"). Write it the way you'd say it to a friend. The wizard refines the language for you in the plan.

**Step 2 — Offering.** Service · Product · Both. This drives pricing rationale ("per project" vs "per unit"), and it changes which roadmap tasks Three Little Birds generates (e.g., manufacturing milestones vs service delivery scripts).

**Step 3 — Market.** Age range, gender, geography. Be specific. "USA" is fine; "USA, suburban families in Texas / Florida / Arizona" produces sharper competitive analysis. Geography also seeds the Compliance scan later — set it correctly so we surface the right state/local items.

**Step 4 — Plan.** Timeframe (3 / 6 / 12 / 24 months to launch), Y1 revenue target, and personal investment. **Be honest about Y1 revenue.** Inflated numbers trigger the Health Check's "unrealistic ramp" warning. If you're unsure, anchor low and let Three Little Birds suggest a higher number based on capacity — you can always edit Pricing rows later. The **Personal investment** number is the cash you're putting in yourself (savings, personal loans, family). It anchors runway math + Three Little Birds's infrastructure recommendations: a $5K self-funded plan gets leaner ops than a $250K backed one.

**Step 5 — Review.** Final read-through. You can edit any field — nothing is committed until you click **Generate Plan**.

---

## Part 3 — During generation (~30-60 sec)

A "Drafting…" modal shows two parallel progress bars:
- **Plan essentials** — 3 sections generated in parallel: refined description, competitive analysis, pricing model.
- **Roadmap** — 12-16 phased tasks across To Do · In Progress · Done (Standard+ tier).

After essentials land, the backend also kicks off **Plan Health Check** and **Goal draft suggestions** automatically — both appear on the plan page within a few seconds of navigation. You don't need to click anything.

If anything fails, you'll see an inline retry — re-runs are free (they don't count against the 3-per-section regeneration cap).

---

## Part 4 — Reading the Plan (10-15 min)

Your plan has 9+ sections. Here's the founder's lens on each.

### 01 · Refined description
Should sound like you, sharper. If it reads generic ("we leverage cutting-edge solutions…") regenerate once with a more specific input. The bolder your Three Little Birds voice setting, the more this section pops.

### 02 · Target market
A re-statement of your inputs with extra texture. No regeneration needed unless your inputs change.

### 03 · Competitive analysis
3-5 named competitors with strengths/weaknesses and the **"Our edge"** line. Two questions to ask:
1. Are these *real* competitors or generic placeholders? If generic, your industry/geography inputs were probably too broad — regenerate after tightening them.
2. Is "Our edge" defensible or just wishful? Defensible edges are price, distribution, a specific operational advantage. "Better customer service" is not defensible.

### 04 · Pricing Model
Each row has a name, price, unit, and Three Little Birds rationale. **The Pricing Scenario Bar** at the top lets you snapshot up to 6 pricing strategies — try one optimistic, one conservative, one bundle — and compare Y1 revenue impacts side-by-side without losing your numbers.

If you edit any price, the **3-Year Revenue Projection** locks until you either *adopt* your edits as the new baseline or *reset* to Three Little Birds prices. This keeps revenue honest.

### 05 · 3-Year Revenue Projection
Year-by-year revenue with assumptions. Drag the **Demand-retention slider** (0-100%) to model conservative vs aggressive retention. The chart redraws live. If Y3 looks wildly optimistic, that's a red flag — investors will pressure-test this number, and a 10x Y1→Y3 jump without a credible story will hurt you.

### 06 · Customer Acquisition
Ranked 1-5 by *cost to test*. Treat #1 and #2 as your "this month" channels. Anything below #3 is a "later" channel — don't try to do all five at once.

### 07 · Infrastructure
Three columns: Personnel · Technology · Administration. Each row is a thing you'll need to operate. Cross-reference this with your Budget — if Infrastructure says "1 part-time bookkeeper" but Budget shows $0 for accounting, fix one of them.

A single **Regenerate infrastructure** button refreshes all three columns together as a unit (so personnel, technology, and admin stay coherent). It's capped at **one regeneration** per business — far stricter than the 3-per-section cap on other sections, since infra changes ripple into the Budget. If you need another pass, open the wizard and edit the **Description**: changing the description automatically unlocks a fresh infra regeneration, because the new framing is what should drive the new staffing / tools list.

### 08 · Plan Health Check (auto-generated)
A 0-100 score blending:
- **50% deterministic** — computed from your data across 4 dimensions (Runway, Budget completeness, Revenue realism, Roadmap balance).
- **50% LLM qualitative** — Three Little Birds's judgment on founder fit, strategy coherence, market timing.

Below the score are **3 risks** (ordered by severity) and **3 this-week actions** ordered by leverage. Treat the actions as your next 7-day priority list. The Health Check has **no regeneration cap** — re-run it whenever your plan evolves.

### 09 · Goals (auto-generated drafts)
3-5 SMART goals pre-drafted in the **drafts banner** at the top of the Goals section. Review each:
- **Keep** — saves it as an active goal with a progress tracker.
- **Skip** — dismisses it.

Once you've decided on all drafts they disappear (server-side too, so they don't reappear on next visit). You can manually add unlimited goals later. Each goal has a kind (revenue · customers · launch · product · hiring · funding · operations) and auto-status (not_started → in_progress → completed → at_risk based on progress vs target).

---

## Part 5 — Iterate without burning credits (5 min)

Each plan section can be regenerated up to **3 times** per business (badge: "N/3"). Once capped, the section locks. Two safety valves:
- **Plan Health Check** is unlimited — re-run as your plan evolves.
- **Pricing Scenario Bar** lets you A/B/C pricing without burning the revenue-projection regen.

If you hit the cap and *really* need a reset, an admin can clear it via the Recent Jobs Tray ("Reset regen counts"). Otherwise: **edit instead of reroll**. Manual edits are unlimited.

---

## Part 6 — What to do after the plan reads well

1. **Roadmap** (Standard+ tier) — drag tasks across the launch board. Each card auto-stamps dates on move and supports per-task comments + Three Little Birds service-link suggestions (Stripe Atlas, ZenBusiness, Mercury, etc.).
2. **Budget vs Actuals** — log real monthly spend per category, or upload a receipt and Three Little Birds will OCR vendor/date/amount/category for you.
3. **Compliance** — scan for federal, state, and industry-specific regulatory items. Each item has a due date that flows into the **Deadlines widget** on your dashboard.
4. **Grants** — auto-match against Grants.gov + Three Little Birds-sourced state/local/private programs. Saved grants get a 6-10 question intake wizard that drafts a full markdown application for you.
5. **Investor Marketplace** (Standard+, Health > 50, 3+ months budget burn) — opt-in to be discoverable by registered investors with matching industry interests.

---

## Quick reference

| Action | Where |
|---|---|
| Edit any field | Click directly on the value |
| Regenerate a section | "Regenerate" button on each section header |
| Run Health Check | Section 08, "Run health check" |
| Suggest more goals | Section 09, "Suggest with Three Little Birds" |
| Snapshot a pricing scenario | Pricing Scenario Bar → "+ Save scenario" |
| Export PDF | Top bar → "Export plan" (Basic = watermarked, Standard+ = clean) |
| Ask the Founder Mentor | Bottom-right floating button on any page |

That's the whole loop. Wizard → read → iterate → execute. Welcome to the founder's life.`,
  },
  {
    slug: "starting-without-experience",
    title: "Starting a business with zero experience",
    category: "getting-started",
    summary: "What to do when you've never run anything before.",
    body: `# Starting a business with zero experience

The hardest part isn't knowing what you don't know — it's not knowing what to *learn first*. Three Little Birds collapses that order for you.

## Your first hour
1. Complete the wizard (10-15 min)
2. Read your generated plan top-to-bottom — don't edit yet, just understand
3. Run **Plan Health Check** — it scores your plan 0-100 and lists the 3 biggest risks
4. Run **Compliance scan** — see exactly what licenses, registrations, and filings your industry needs
5. Run **This Week's Focus** — get the 3 most important things to do *this week* across plan, compliance, roadmap, and goals

## Your first week
- Talk to 5 potential customers (your roadmap will have a task for this)
- File your business entity if you're sure on the name (use the in-app **Service Agent** for ZenBusiness or Stripe Atlas)
- Start a separate bank account (Mercury agent walks you through it)

You don't need an MBA. You need a sequence. Three Little Birds gives you the sequence.`,
  },
  {
    slug: "fear-of-failure",
    title: "Working through fear of failure",
    category: "getting-started",
    summary: "Practical exercises when the fear of starting feels paralyzing.",
    body: `# Working through fear of failure

Fear is loudest when the path is foggy. The cure isn't courage — it's clarity.

## Make the worst case concrete
Open the **Budget** module. Put in your real numbers — startup costs, monthly burn, your savings runway. Now you can see the actual financial worst case in months, not in a vague feeling. Most founders discover their worst case is "I waste 4-6 months and I'm back where I started" — which is far less scary than "I lose everything."

## Ship something tiny in week one
Your roadmap has a "Customer interviews" task. Do five of them. You don't need a logo, a website, or an LLC. You need 5 people to tell you whether the problem you're solving is real.

## Use the Mentor
Click the orange **Ask mentor** button (bottom-right) and tell it exactly what you're afraid of. It can see your plan, your runway, and your roadmap — it can tell you which fears are statistical reality and which are catastrophizing.

## Remember the three little birds
Don't worry about a thing. Every little thing is gonna be alright. (And the ones that aren't, we have a roadmap for.)`,
  },
  {
    slug: "anatomy-of-a-plan",
    title: "Anatomy of a business plan",
    category: "plan",
    summary: "Every section explained — what it's for, what good looks like.",
    body: `# Anatomy of a business plan

Your generated plan has 8 sections. Here's what each one is *for*:

- **Refined description** — your elevator pitch tightened
- **Competitive analysis** — who else does this, where their cracks are
- **Pricing model** — what you charge and why
- **3-year revenue projection** — Y1, Y2, Y3 with assumptions you can challenge
- **Customer acquisition** — how you find your first 100, then 1000 customers
- **Infrastructure** — tools, team, vendors you'll need to operate
- **SWOT** — internal strengths/weaknesses, external opportunities/threats
- **Plan Health Check** — Three Little Birds-graded score with risks and this-week actions

If a section feels wrong, hit **Regenerate** on just that section. Don't redo the whole plan.`,
  },
  {
    slug: "regenerating-sections",
    title: "When (and how) to regenerate a section",
    category: "plan",
    summary: "Iterate without losing edits — note the 3-attempt cap per section.",
    body: `# When (and how) to regenerate a section

Each plan section has a small **Regenerate** button. Use it when:

- The section feels generic ("we will leverage synergies…")
- Your inputs changed (you found a new competitor, target market shifted)
- You want a different angle (more conservative pricing, more aggressive growth)

Regenerate keeps every other section intact. It also re-runs against your latest founder profile and Three Little Birds voice settings, so updating those before regenerating produces noticeably different output.

## The 3-regeneration cap (and the stricter Infrastructure rule)

Most plan sections are capped at **3 regenerations** per business — visible as an "N/3" badge above the Regenerate button. Once you hit the cap, the button greys out and the label flips to "Regenerate locked". This keeps LLM costs predictable and gently nudges you to *edit* rather than reroll forever.

**Three exceptions:**
- **Plan Health Check** has no cap — re-run it as often as you want as your plan evolves.
- **3-Year Revenue Projection** is locked the moment you edit any pricing row. The system points you at "adopt or reset pricing first" so the projection stays honest to your numbers. Use the **Pricing Scenario Bar** (snapshot up to 6 strategies) to compare alternates without burning your one revenue-projection regenerate.
- **Infrastructure** uses a stricter cap of **1 regeneration** because the three columns (Personnel · Technology · Administration) regenerate as a single unit and feed directly into the Budget. To unlock another pass, edit the **Description** on the wizard — that change automatically resets the infrastructure counter so the new framing drives a fresh staffing / tools list.`,
  },
  {
    slug: "kanban-roadmap",
    title: "Working the launch board",
    category: "execution",
    summary: "Drag-drop tasks, schedule dates, attach notes & service agents.",
    body: `# Working the launch board

Three columns: **To Do · In Progress · Done**. Drag any card across columns — moving to In Progress stamps a start date, moving to Done stamps completion. Drag works with a mouse on desktop and with a **long-press** on phones and tablets; or just tap the **To Do / Working / Done** buttons on a card to move it without dragging.

Each card supports:
- **Notes** (click the message icon)
- **Calendar dates** (start + due)
- **Comments thread** with real-time WebSocket sync if you have collaborators
- **Find services** — tap *Find services* on any task to get 2-4 Three Little Birds-matched vendors (Stripe Atlas, ZenBusiness, Mercury, Gusto, Shopify…), each with its own in-app agent. See the *Find Services* article for the full walkthrough.

Add custom tasks any time with **+ Add task**.`,
  },
  {
    slug: "industry-templates",
    title: "Apply an industry template",
    category: "execution",
    summary: "One-click roadmap templates for 8 industries.",
    body: `# Apply an industry template

If your generated roadmap doesn't cover an industry-specific milestone (food handler permits for restaurants, FDA compliance for food brands, MLS access for real estate…), open **Templates** and apply a curated industry template. We dedupe by title so existing tasks aren't duplicated.`,
  },
  {
    slug: "compliance-tracker",
    title: "The Compliance & Regulation tracker",
    category: "execution",
    summary: "Federal + state + city + industry-specific filings, tracked.",
    body: `# Compliance tracker

Every business has 8 federal baseline items (EIN, FLSA, OSHA, ADA, FinCEN BOI, EEOC, privacy, federal income tax) plus Three Little Birds-generated state/city/industry items. Each carries a severity (high/medium/low) and a status state machine: not_started → in_progress → completed / not_applicable.

Your **Compliance Risk Score** badge (Dashboard) summarizes coverage — score floors at 79 if any HIGH-severity item is open, regardless of how many low-priority items you've completed. That's intentional: a missing EIN matters more than a finished privacy policy.`,
  },
  {
    slug: "weekly-focus",
    title: "How This Week's Focus works",
    category: "execution",
    summary: "Three ranked actions, refreshed weekly, tied to your data.",
    body: `# How This Week's Focus works

Click **Get this week's focus** on any plan-generated card. Three Little Birds reads your plan health, compliance, roadmap, goals, and budget — then picks the 3 highest-leverage actions you should do this week, ranked.

Each action is grounded in real data with a deep-link back to the right page. After 7 days, the focus shows a **Stale** marker — regenerate when your situation has shifted.`,
  },
  {
    slug: "find-services",
    title: "Find Services — get-it-done agents",
    category: "execution",
    summary: "Three Little Birds-matched vendors for any roadmap task, each with an in-app agent.",
    body: `# Find Services

Every roadmap task has a **Get-it-done agents** panel. Tap **Find services** and Three Little Birds reads that specific task plus your plan, then suggests **2-4 real service providers** that can actually do the work — incorporation (Stripe Atlas, ZenBusiness), banking (Mercury), payroll (Gusto), storefronts (Shopify), plus design, marketing, and legal help.

Each suggestion shows:
- A category icon and a short description of what they do
- A **Vetted** tag when it's a curated, hand-checked provider
- A rough **price hint** so there are no surprises
- A one-line brief on how this vendor fits *your* task

## Two ways to act
- **Start with agent** opens an in-app assistant that knows only this one task and walks you through using that vendor step by step — ideal when you're filing your LLC or opening a business account and want guidance for that exact form.
- **Visit site** opens the provider in a new tab.

Tap **Regenerate** any time to pull a fresh set of picks. Find Services is available on Standard and Advanced.`,
  },
  {
    slug: "service-agents",
    title: "Service Agents — in-app vendor assistants for every roadmap task (Standard+)",
    category: "execution",
    summary: "Task-scoped Three Little Birds agents that walk you through a real vetted vendor — Stripe Atlas, ZenBusiness, Mercury, Gusto, Shopify and 20+ more — in Chat or Quick mode.",
    body: `# Service Agents

Every roadmap task in Three Little Birds ships with a **Get-it-done agents** panel. Behind each suggested vendor is a **Service Agent** — a Three Little Birds assistant scoped to *one task* and *one provider*, so instead of a generic chatbot you get step-by-step guidance for that exact filing, signup, or setup.

Service Agents are available on **Standard and Advanced**.

---

## The curated vendor catalog (23+ providers)

Three Little Birds ships a hand-vetted catalog spanning every category a first-time founder actually touches:

- **Legal & incorporation** — Stripe Atlas, ZenBusiness, LegalZoom, Dropbox Sign
- **Finance & payments** — Stripe, Square, Mercury, QuickBooks, Wave
- **HR & payroll** — Gusto, Rippling
- **Product & storefront** — Shopify
- **Design & brand** — Squarespace, Framer, Carrd, Canva, Looka
- **Marketing & sales** — Mailchimp, Beehiiv, Google Business Profile, HubSpot CRM
- **Operations & scheduling** — Calendly, Notion

Each provider carries a category, tag list, price hint, and short description so the picker never feels random. Additional non-curated vendors are Three Little Birds-suggested contextually against your specific task.

---

## Where to find them

1. Open any Business Plan and go to the **Roadmap / Launch board**.
2. Click into any task card.
3. Scroll to the **Get-it-done agents** panel.
4. If services haven't been suggested yet, tap **Find services** — Three Little Birds reads your task title, description, phase, industry and geography, then returns 2–4 matched providers.
5. Each provider card has two buttons: **Start with agent** (opens the in-app Service Agent drawer) and **Visit site** (opens the vendor's website in a new tab).

Curated providers show a **Vetted** tag so you know it's a hand-checked pick.

---

## Two modes: Chat and Quick

### Chat mode — free-form conversation

Ask anything about that vendor for that task. Because the agent is scoped to a single task and provider, answers are grounded and specific:

- *"Do I need to file a BOI report after ZenBusiness incorporates me?"*
- *"How does Mercury's ACH limit compare to Stripe Treasury for my first month of receivables?"*
- *"What Gusto plan should I pick if I'm hiring one contractor before end of quarter?"*

Chat threads persist per-(business × task × provider), so you can leave and come back and the agent remembers your context.

### Quick mode — structured draft in 30 seconds

Every Service Agent has 3–5 **Quick questions** hand-tuned to the task. Answer them once and the agent generates a ready-to-paste draft:

- **Stripe Atlas → LLC filing** — outputs the EIN application summary and Delaware LLC operating-agreement blanks pre-filled.
- **Mercury → business account opening** — outputs the account application checklist with your business type, EIN status and expected first-month receivables.
- **Gusto → payroll onboarding** — outputs the onboarding brief with headcount, W-2 vs 1099 split and pay schedule.
- **Shopify → storefront launch** — outputs the store setup checklist with theme choice, product count, shipping origin and payment gateway.

The Quick output is copyable in one click and reruns instantly if you tweak any answer.

---

## Why the agent stays scoped

Because each agent only knows about ONE task and ONE vendor, it can't drift into unrelated advice or fabricate details about a service it isn't wired to. That focus is what makes the guidance actually usable — you're not fighting a generic assistant that pretends to know everything.

For cross-business, cross-plan strategy questions ("what should I prioritize across all my ventures?"), use the **Founder Mentor** instead — that's the counterpart to Service Agents and lives in the top nav.

---

## Chat history & regeneration

- Every message you send to a Service Agent is saved to that task's thread — refresh the page, close the drawer, come back tomorrow, and the whole conversation is right there.
- Tap **Regenerate** on the Find services panel any time you want a fresh set of vendor picks (e.g. your task's description changed, or you want cheaper / faster / different-jurisdiction options).
- Editors on collaborative plans share the same threads, so an advisor and founder can pick up where each other left off inside the same agent.

---

## Where to find it

- **Per task**: any roadmap card → *Get-it-done agents* → *Find services* → *Start with agent*.
- **Tier**: **Standard or Advanced** (Basic and free see an upgrade prompt on Find services).
- **Related**: the shorter **Find Services** article covers just the vendor-matching layer; this article covers the agent layer that sits on top of it.`,
  },
  {
    slug: "collaboration",
    title: "Inviting collaborators to a plan",
    category: "execution",
    summary: "Add editors and viewers with real-time, live-presence collaboration.",
    body: `# Inviting collaborators

On Standard and Advanced you can invite teammates to a specific business plan. Open the plan, use **Collaborators**, send an invite by email, and pick a role:
- **Editor** — can edit the plan, roadmap, budget, goals, and compliance
- **Viewer** — read-only access

## Real-time by default
When two or more people are on the same plan you'll see live presence, and edits, roadmap moves, and comments sync instantly over a live connection — no refresh needed. Roadmap card comments are threaded, so a conversation stays attached to the exact task it's about.

Owners can change a collaborator's role or remove them at any time. Each plan keeps its own collaborator list, so access stays scoped to exactly what you choose to share.`,
  },
  {
    slug: "in-app-messaging",
    title: "In-app messaging",
    category: "execution",
    summary: "Direct message threads with the people you work with.",
    body: `# In-app messaging

The **Messages** area (in the top navigation) is a lightweight inbox for direct conversations — separate from roadmap comments. Use it to reach a collaborator, an advisor, or support without leaving the app.

- Start a thread, and unread counts appear on the nav badge so you never miss a reply
- Messages are tied to your account, not a single plan, so the conversation follows you across every business you run
- Threads stay in your inbox for easy reference later

If you work with an **advisor**, messaging is the simplest way to ask a quick question between working sessions.`,
  },
  {
    slug: "budget-and-runway",
    title: "Budget, burn rate, runway",
    category: "money",
    summary: "Three Little Birds-drafted startup costs + monthly burn, with monthly actuals.",
    body: `# Budget, burn rate, runway

The Budget module starts with Three Little Birds-drafted line items based on your industry and target market. Edit anything — categories, amounts, descriptions.

**Runway** = (Startup capital + Cash on hand) / Monthly burn.

The **Budget vs Actuals** panel lets you log real monthly spend per category and see variance from plan. Use the **Receipt OCR** dialog to drop in a receipt image — Three Little Birds extracts vendor/date/amount/category and merges it into the right month.`,
  },
  {
    slug: "quickbooks-xero-sync",
    title: "Sync QuickBooks Online & Xero (Standard+)",
    category: "money",
    summary: "Connect your accounting tool in one click and merge real transactions into Budget Actuals — read-only, with a CSV fallback.",
    body: `# Sync QuickBooks Online & Xero

On **Standard and Advanced**, you can keep your Budget grounded in reality by pulling actual transactions straight from your accounting software — no manual re-typing.

---

## Connect in one click

Go to **Settings → Accounting integrations** (or **/settings/integrations**). You'll see a card for **QuickBooks Online** and one for **Xero**:

- **Connect** kicks off a secure OAuth login with the provider. You authorize once.
- We request **read-only** access. We **never write anything back** to your books.
- **Disconnect** any time — your access tokens are deleted immediately.

If a provider shows **"Coming soon"**, one-click connect isn't enabled for your account yet — use the CSV import below in the meantime (it produces the exact same result).

## Sync, then apply to Budget

1. Hit **Sync now (30 days)** to pull your most recent transactions.
2. Open **Apply synced data to Budget Actuals**, pick the business and the months you want, and review the per-month revenue / expense / transaction counts.
3. **Apply** merges those months into your Budget Actuals under a single **"Imported (QBO / XERO)"** category line.

Re-applying is **idempotent** — it overwrites the imported line for those months rather than doubling it, so you can re-sync safely whenever new transactions land.

## CSV import fallback

No live connection? The **CSV import guide** at **/help/qbo-xero-import** walks you through exporting from QuickBooks or Xero and mapping the columns — about three minutes of work for the same outcome.

## Where to find it

- **Settings → Accounting integrations** → **/settings/integrations**
- CSV guide: **/help/qbo-xero-import**
- Tier: **Standard or Advanced** (it feeds the Budget module, which is Standard+).`,
  },
  {
    slug: "grants",
    title: "Finding & applying for grants",
    category: "money",
    summary: "Live federal grants + Three Little Birds-sourced state/private programs.",
    body: `# Finding & applying for grants

Open **Grants** → **Match** to pull a fresh list. We hit Grants.gov for federal opportunities and Three Little Birds for state, local, and private programs (Hello Alice, Amber Grant, IFundWomen, Comcast RISE…). Each match is scored against your business.

**Save** any grant → click into it → run the **Application Wizard**. Three Little Birds generates 6-10 tailored intake questions, then drafts a complete markdown application with Executive Summary, Need, Project Description, Budget Justification, Team, Impact, and Eligibility Attestation sections.

Track status: saved → drafting → submitted → under_review → awarded / rejected / withdrawn. The **Board** tab is a board view of your saved grants for the currently-selected Business Plan — pick a different plan from the business selector at the top of the page to switch the board's scope. Each grant on the board is anchored to one plan, so cross-plan opportunities stay cleanly separated.`,
  },
  {
    slug: "lender-financing-package",
    title: "Lender-ready financial package (Standard+)",
    category: "money",
    summary: "Generate the bank-ready financial statements a lender needs — P&L, cash flow, balance sheet, Sources & Uses, break-even and DSCR — in one PDF.",
    body: `# Lender-ready financial package

When you apply for a small-business loan or line of credit, a bank wants more than a narrative — they want **projected financial statements** they can underwrite. On **Standard and Advanced**, Three Little Birds generates them for you from your existing plan and budget.

---

## Where to find it

Open a business plan → click **PLAN ACTIONS** (top-right) → **Financing**. (Direct link: **/business/&lt;id&gt;/financing**.)

## What you enter

A short **financing request**:
- **Loan amount**, **term (months)**, and **APR**
- **Equity injection** (your own money going in — prefilled from your wizard's Personal Investment)
- Optional **Advanced assumptions**: COGS %, tax rate, A/R & A/P days, CapEx depreciation life, Year-1 revenue ramp, and the fixed share of operating expenses. Sensible defaults are filled in, so you can generate a package in seconds.

## What you get

Every figure is **formula-driven (no Three Little Birds)** so the statements tie out and a loan officer can trace them:

- **Loan summary + Sources & Uses** — level monthly payment, total interest, and a balanced sources/uses table.
- **Projected Income Statement (P&L)** — Year-1 monthly detail plus 3 annual years (revenue, COGS, gross profit, opex, EBITDA, depreciation, interest, tax, net income).
- **Projected Cash Flow** — operating / investing / financing, ending cash by year.
- **Projected Balance Sheet** — opening plus three year-ends; **assets equal liabilities plus equity for every period**.
- **Break-even** revenue (annual + monthly) at your contribution margin.
- **DSCR** (Debt Service Coverage Ratio) per year — most lenders want **≥ 1.25x**.
- **Key assumptions** page documenting every input.

## Borrower forms included

The PDF appends blank, fillable templates that lenders always ask the borrower to complete: a **Personal Financial Statement** summary, a **Debt Schedule**, and a **Collateral Schedule**.

## Export

Click **Download bank PDF** for one branded document (it honors your Brand Kit logo and colors). The numbers are most accurate once you've generated your **3-year revenue projection** and your **Budget** — otherwise the model uses clearly-labeled fallbacks.

## What the lender still needs from you

These come from you, not the app: signed personal financial statements, 2-3 years of tax returns (for an existing business), bank statements, proof of equity injection, and personal credit / guarantee. The package gives you everything else.

> Tier: **Standard or Advanced**.`,
  },
  {
    slug: "investor-marketplace",
    title: "Investor scenarios & the marketplace",
    category: "money",
    summary: "Three Little Birds-modeled funding deals you can compare, save, and add to your deck.",
    body: `# Investor scenarios and the marketplace

On **Advanced**, the Funding Strategy module can generate **investor scenarios** — Three Little Birds-modeled deals tailored to your plan across **equity, income-share, and hybrid** structures, each projected over **3, 5, and 7-year** horizons.

## Compare and act
- View scenarios side by side to see how ownership, payback, and founder take-home differ
- **Add to deck** drops a selected scenario straight into your Pitch Deck PDF
- Save your strongest structures to a **personal scenario library** to reuse on future plans

## The marketplace
A featured **marketplace** lets you browse curated, anonymized scenario templates for inspiration — apply one to your own plan as a starting point, then let Three Little Birds re-fit the numbers to your business. It refreshes regularly so the examples stay current.

Investor scenarios and the marketplace are an Advanced-tier feature; everything stays private to your account unless you choose to publish a template.`,
  },
  {
    slug: "pricing-plans",
    title: "Pricing — Basic vs Standard vs Advanced (+ trial)",
    category: "account",
    summary: "Three paid tiers + a 7-day free Standard trial. What unlocks where.",
    body: `# Pricing plans

Every new account starts with a **7-day free Standard trial** — full access to compliance, grants, mentor, plan health, and budget tracking. No card required. During the trial five sections are shown as **samples** so you can see exactly what subscribing unlocks: Competitive Analysis, Customer Acquisition, Infrastructure, Pricing Model, and 3-Year Revenue Projection. Your real Three Little Birds-generated numbers for those sections are reserved for paid plans (subscribing flips them instantly — no re-generation). After the trial you pick one of three paid tiers.

## Basic — $15 / mo · $165 / year (save $15)
For one founder testing the waters.
- Up to **5 active business plans**
- Wizard + Three Little Birds-generated plan
- **Goals & weekly focus**
- **Plan PDF** export (watermarked)
- Roadmap browsing (no launch-board editing or templates)

## Standard — $25 / mo · $275 / year (save $25) — *most chosen*
The full launch OS.
- Up to **2 active plans** (up to **20 total** — read-only when inactive)
- Everything in Basic, plus…
- **Launch board** (drag, schedule, notes, templates)
- **Budget & burn-rate** tracking with monthly actuals + receipt OCR
- **QuickBooks Online & Xero sync** — one-click, read-only import into Budget Actuals (+ CSV fallback)
- **Compliance & regulation tracker** (federal + state + city + industry)
- **Grants finder** + application wizard
- **Founder Mentor** (cross-business advisor)
- **Plan Health Check** (hybrid 50/50 deterministic + LLM score)
- **In-app Service Agents** (Stripe Atlas, ZenBusiness, Mercury, Gusto, Shopify…)
- **Team collaborators** (editor / viewer roles, real-time WebSocket sync)
- **Clean Plan PDF + Pitch Deck PDF** (10-slide investor-ready landscape deck)
- **Public share link** with branded social-preview card
- **Industry benchmarks** beneath Health Check
- **Funding Strategy** module (self-funded · grants · investors)
- **Native CRM & sales pipeline** (contacts, companies, drag-and-drop deals; closed-won feeds Goals + Runway)
- **Document Library** (secure per-plan & workspace vault with Three Little Birds analyze, classify & tag)
- **Three Little Birds logo generation** (Brand Kit, Gemini-powered — up to 2 per account)

## Advanced — $35 / mo · $385 / year (save $35)
Unlimited plans for serial founders & agencies.
- Everything in Standard, plus…
- **Unlimited business plans**
- **Investor scenarios** — Three Little Birds-generated equity / income / hybrid deals across 3-5-7 year horizons, side-by-side compare, one-click "Add to deck"
- **Personal scenario template library** + a featured marketplace
- **Pitch-deck integration** for selected investor scenarios
- **Email & SMS Campaigns** — reusable templates, target your CRM contacts, schedule send windows, and track delivered / opened / clicked

## What happens at expiry
If you subscribed and then lapsed, your data is preserved indefinitely. The dashboard shows an "EXPIRED" banner with a Resubscribe button; business cards remain visible but plan details lock until you re-subscribe. Compliance CSV / ICS exports stay available even after expiry so you can keep your filing reminders.

If you did NOT convert from the free trial, your business plans are **permanently deleted 21 days after the trial ends**. Subscribing any time within that 21-day window unlocks everything immediately with your real Three Little Birds-generated numbers intact — including the five sections that showed sample data during the trial. After 21 days the data is gone and you'd need to run the wizard again from scratch.

Upgrade or change cycle any time from the **Billing** page. On a real Stripe key you also get a self-service customer portal (cancel, upgrade, update card).`,
  },
  {
    slug: "brand-kit",
    title: "Brand Kit — your logo, colors & branded exports",
    category: "account",
    summary: "Set a logo, tagline, and colors that flow into every PDF and share link.",
    body: `# Brand Kit

Open **Settings** and find the **Brand Kit** to make every export look like *yours*:
- Upload a **logo** (or let Three Little Birds generate one from your business name)
- Set a **tagline** and your **brand colors**

Once set, your Brand Kit is applied automatically to:
- The **Plan PDF** and **Pitch Deck PDF** — branded headers and accent colors
- Your **public share link** — the social-preview card shows your logo and colors

This keeps investor- and partner-facing documents consistent without any design work. Brand Kit settings live at the account level, so they apply across all of your plans, and you can update them any time.`,
  },
  {
    slug: "plan-health-check",
    title: "How the Plan Health Check score works",
    category: "plan",
    summary: "Hybrid 50% deterministic + 50% LLM judgment, broken down across 5 components.",
    body: `# How the Plan Health Check score works

The score on the Health Check tile is a **hybrid 0-100** — 50% deterministic and 50% qualitative LLM judgment. The deterministic half is computed from real data so the score is stable run-to-run; the LLM half captures founder fit, strategy coherence, and market timing that no formula can quantify.

You'll see a **Data N · Research N** split badge on the score tile that shows both halves transparently.

## The four deterministic components (25 pts each)

1. **Runway** — Startup costs ÷ Monthly burn = months of runway. 12+ months earns full marks, 0 months earns 0. Uses the same blended actuals + planned math as the Budget module if you've logged any actuals.
2. **Budget completeness** — Have you listed startup costs? Monthly burn? Logged any actuals at all? Fully reconciled actuals month-by-month?
3. **Revenue realism** — Compares your stated Y1 target, your Three Little Birds-modeled Y1 revenue, and an implied revenue from (Y1 customers × average pricing × 12). Closer alignment = higher score. Wildly optimistic targets get caught here.
4. **Roadmap balance** — Percentage of roadmap items already shipped (moved to the "Done" column). 0 shipped = 0 pts, all shipped = 25 pts, linear in between. Rewards actual execution over paper planning.

*Customer acquisition was removed Feb 17 · 2026 — founders can't reliably predict which channels will work, and scoring guessed channels was penalising honest uncertainty.*

The **"What drives the score"** panel under the main number renders all four as progress bars with the actual reasoning ("8.2 months runway — adequate", "6 of 10 roadmap items shipped — 60%") so you see exactly which lever to pull.

## The LLM half

Three Little Birds reads the plan, budget, roadmap, founder profile, AND the deterministic block above. Its job is qualitative: is this a coherent strategy for *this* founder in *this* market right now? It also returns **3 risks** (severity-ranked) and **3 this-week actions** that the dashboard surfaces as the "Focus" list.

## Why no regen cap?

Plan Health is the one section *exempt* from the 3-regenerations-per-section cap. Re-run it as often as you want — after you fix a risk, log new actuals, or refine pricing. The score moving over time is the signal.

## Industry benchmarks (Standard+)

When you're on Standard or Advanced and a healthy cohort exists for your industry, an **Industry benchmarks** strip appears below the Health Check showing how your score and each component compare to anonymized peers. The cohort is rebuilt nightly from real plans (your data is anonymized before contributing).`,
  },
  {
    slug: "health-scores",
    title: "The six Health scores in Three Little Birds",
    category: "getting-started",
    summary: "Every 0–100 score you'll see across the app — Plan, Launch, Business, Operating, Financial and Growth Health — with the exact weights, per-leg formulas, thresholds and click-through pills that turn each score into a next move.",
    body: `# The six Health scores

Three Little Birds surfaces **six 0–100 Health scores** across the app. Each one answers a different question about your business, and every leg of every score is a **click-through pill** wired to the feature that drives it — so the score is never a dead-end.

Every leg's formula is documented below so you can look at any red pill and know **exactly** what to change to move it.

---

## 1 · Plan Health · *"Is my written plan investor-grade?"*

**Where:** the Health Check tile on your Business Plan.
**Formula:** hybrid **50% deterministic + 50% LLM**.
The deterministic half scores 4 real-data components out of 25 points each:

| Component | Weight |
|---|---|
| Runway | 25 |
| Budget completeness | 25 |
| Revenue realism | 25 |
| Roadmap balance | 25 |

Customer acquisition was removed Feb 17 · 2026 — founders can't reliably predict which channels will work, and scoring guessed channels was penalising honest uncertainty. The LLM half is a qualitative judgment on founder fit, strategy coherence, and market timing. Exempt from the 3-regenerations cap — re-run it whenever your plan evolves.

### How each Plan Health leg is calculated

**Runway (25 pts):**  **startup_costs ÷ monthly_burn** = months. Scored in bands: **≥12 mo → 25**, **≥9 → 21**, **≥6 → 17**, **≥3 → 12**, **≥1 → 6**, **<1 → 0**. If no budget: 0. If burn is zero: 12 (unknowable). Respects your Actuals mode (planned / blend / actuals-only) so the same runway shows on the Budget tile.

**Budget completeness (25 pts):** additive. Startup costs listed → **7 pts**, monthly burn listed → **7 pts**, actuals fully tracked on every line → **6 pts** (or **3 pts** if partial). Scaled to 25 so the max still lines up with the other three legs.

**Revenue realism (25 pts):** compares Y1 modeled revenue against two independent signals — your wizard target and your own units × price × 12 implied revenue. Score = average of **min/max** ratios × 25. **Perfectly aligned = 25**, wildly off = 0. Missing Y1 or pricing = 8 (partial credit).

**Roadmap balance (25 pts):** **shipped_pct = done_items / total_items**. Scored as a straight percentage of 25. **All items shipped = 25**, none shipped = 0, empty roadmap = 0. Rewards actual execution, not "having a nice-looking plan".

**LLM half (50 pts):** Three Little Birds reads your whole plan and grades qualitatively on founder fit, strategy coherence, market timing, and 3 penalty triggers (unrealistic Y1 vs capacity, <3 months runway, missing compliance for your industry, no customer-acquisition motion, roadmap concentration).

## 2 · Launch Health · *"Am I ready to open the doors?"*

**Where:** the Launch Health hero at the top of the Business Hub (pre-launch founders only).
**Formula:** plain **average of three legs** — Plan progress, Roadmap progress and Compliance progress.

| Leg | Weight | Click routes to |
|---|---|---|
| Plan | 33% | Business Plan |
| Roadmap | 33% | Roadmap board |
| Compliance | 34% | Compliance page |

The moment you flip your plan to Operate, this hero is replaced by **Operating Health** (below).

### How each Launch Health leg is calculated

**Plan (33%):** **done_sections / 10 × 100**. The 10 canonical sections that count: Executive Summary, Refined Description, Market Analysis, Competitive Analysis, Customer Acquisition, Pricing Model, Revenue Projection, Personnel, Technology, Administration. A section "counts as done" when it's a non-empty string.

**Roadmap (33%):** **done_items / total_items × 100**. Only items with **status = "done"** count. Empty roadmap = 0. Move something to Done and this number moves immediately.

**Compliance (34%):** **(done + not_applicable) / total_items × 100**. Marking an item "not applicable" counts the same as completing it — because deciding an item doesn't apply to your business is still a completed decision.

## 3 · Business Health · *"Are the fundamentals solid right now?"*

**Where:** the Business Health hero (below Launch/Operating Health on the Hub).
**Formula:** three equal-weight pillars.

| Pillar | Weight | Click routes to |
|---|---|---|
| Launch readiness | 33% | Roadmap |
| Funding | 33% | Plan · Funding section |
| Marketing | 34% | CRM |

If your plan is in **Launch or Operate** mode and this score slips below **65%**, the hero turns bright red with an *Attention needed* band — the fundamentals aren't holding and the app names the weakest leg so you know exactly where to click first.

### How each Business Health leg is calculated

**Launch readiness (33%):** exact same value as the Launch Health score above — a simple average of Plan progress, Roadmap progress and Compliance progress.

**Funding (33%):** the full Financial Health % (see section 5) rolled into the pillar. Includes Runway, Capital stack, Net-vs-plan and Marketing.

**Marketing (34%):** the full Growth Health % (see section 6). Includes Audience, Reach, Conversion and Pipeline.

The Move-of-the-week banner picks whichever pillar drove the biggest week-over-week delta and names it: *"Marketing carried you this week (+27%)."*

## 4 · Operating Health · *"Is the live business trending up?"*

**Where:** the Operating Health hero (ongoing founders only — replaces Launch Health).
**Formula:** four weighted legs. Missing legs contribute **0** to the numerator and the denominator stays pinned at the full weight sum, so a plan with sparse signals scores honestly low rather than re-normalising missing evidence away.

| Leg | Weight | Click routes to |
|---|---|---|
| Revenue trend | 30% | Performance |
| Financial Health | 30% | Plan · Funding section |
| Marketing | 20% | Campaigns |
| Launch Health | 20% | Roadmap |

A **week-over-week delta chip** sits next to the big percentage — green ↑ when trending up, red ↓ when slipping, grey — when flat or fresh.

### How each Operating Health leg is calculated

**Revenue trend (30%):** compares the last 3 months of revenue actuals to the prior 3 months. **ratio = recent3 / prior3**, clamped 0–2, then **score = ratio ÷ 2 × 100**. So **flat = 50**, **doubled = 100**, **halved+ = 0**. Prior 3 months = 0 and recent > 0 (brand-new revenue) → **100** (big positive signal). Both zero → null → counts as 0.

**Financial Health (30%):** the full Financial Health composite (Runway, Capital stack, Net vs plan and Marketing — see section 5). Rolled in at 30% because runway and capital availability are the fastest way to kill an operating business.

**Marketing (20%):** the full Growth Health composite (Audience, Reach, Conversion and Pipeline — see section 6). Rolled in at 20% because a strong marketing pipeline is the leading indicator of the revenue that keeps the runway equation solvent.

**Launch Health (20%):** blend of Plan progress, Roadmap and Compliance completion. Included at 20% because even operating businesses need their plan sections, milestones and legal filings kept current.

## 5 · Financial Health · *"Is the money picture holding up?"*

**Where:** the strip at the top of the Funding tile on the Hub.
**Formula:** four equal-weight legs (25% each). Missing signals honestly drag the number down — no fake credit for legs you haven't filled in.

| Leg | Click routes to |
|---|---|
| Runway | Plan · Budget |
| Capital stack | Plan · Funding section |
| Net vs plan | Performance |
| Marketing | Campaigns |

### How each Financial Health leg is calculated

**Runway (25%):** reads **calcRunway.status** (the same status used to colour the Runway KPI tile). **infinite / ok → 100**, **watch (3–6 mo) → 60**, **critical (< 3 mo) → 20**, **unknown → 0**.

**Capital stack (25%):** measures how much of your cash is non-personal (investors + awarded grants + CRM won + funded loans) vs your base savings.

  - No non-personal capital → **20**
  - No base savings but you have non-personal capital → **100**
  - Non-personal capital ≥ base savings → **100**
  - Otherwise → **60**

*Awarded grants roll up here rather than getting a leg of their own — they add to your non-personal capital, so a dedicated Grants leg was double-counting the same signal.*

**Net vs plan (25%):** **performance_summary.net.favorable** — is this month's revenue-minus-burn actual beating the plan? **Favourable → 100**, unfavourable → **30**. Missing (no actuals logged yet this month) → **0**.

**Marketing (25%):** the full Growth Health composite (Audience, Reach, Conversion, Pipeline — see section 6). Rolled in as a Funding leg because marketing pipeline is the leading indicator of the revenue that keeps runway solvent.

## 6 · Growth Health · *"Is traction building?"*

**Where:** the strip at the top of the Marketing tile on the Hub (below the description, above the CRM ribbon).
**Formula:** four equal-weight legs (25% each).

| Leg | Click routes to |
|---|---|
| Audience | CRM |
| Reach | CRM |
| Conversion | Campaigns |
| Pipeline | CRM |

### How each Growth Health leg is calculated

Every leg reads from **crm_rollup** — a denormalised summary on the business doc, refreshed on every CRM save — so the Hub stays a single-fetch page.

**Audience (25%):** total CRM contacts (**open_count + won_count**). **≥ 5 → 100**, **≥ 1 → 60**, **0 → 0**. First contact is the biggest jump in the ladder — a real audience starts with the first name.

**Reach (25%):** distinct customer segments that have generated at least one won deal (**won_by_segment.length**). **≥ 2 segments → 100**, **1 → 60**, **0 → 20**. Two won segments proves your ICP guess wasn't just one lucky friend.

**Conversion (25%):** closed-won deal count. **≥ 3 wins → 100**, **≥ 1 win → 60**, **open pipeline but no wins yet → 30**, **nothing → 0**. Deliberately weights the first win heavier than subsequent ones.

**Pipeline (25%):** weighted pipeline dollar value (**sum(deal_value × stage_probability)** for every open deal). **≥ $10,000 → 100**, **≥ $1,000 → 60**, **> $0 → 30**, **empty → 0**. Reads pipeline value the same way any sensible CRM does — the higher the stage probability, the more of the deal we count.

---

## Colour bands (shared vocabulary)

Every Health score uses the same colour ladder so the surface reads consistently:

- **Green · Strong / Thriving** — ≥ 75%
- **Amber · Building** — 50–74%
- **Red · Fragile** — < 50%
- **Grey · —** — no plan yet, missing data, or unknown

Business Health adds one extra: a **bright red critical band** when your plan is live (Launch or Operate) and the composite drops below 65%.

## Why the pills are clickable

Every leg is a link to the feature that raises the number. See a red Runway pill? Click it — you land straight on the Budget section, ready to trim burn or add revenue. That's the whole point: a score you can't act on is a distraction.`,
  },
  {
    slug: "mentor",
    title: "Using the Founder Mentor",
    category: "ai",
    summary: "A cross-business advisor that sees everything you've built.",
    body: `# Using the Founder Mentor

Click the orange **Ask mentor** button (bottom-right) on any page. The mentor:

- Reads every business you've created (plan, goals, compliance, focus, budget)
- Refreshes context on every message — so it always sees your latest state
- Speaks in your voice (whatever you set in **Settings → Three Little Birds voice**)
- Persists conversations — switch between threads in the left rail

Good first questions:
- "What should I prioritize this week across all my businesses?"
- "Which plan has the weakest financial story and how do I fix it?"
- "Draft a 3-sentence investor update covering my latest progress."`,
  },
  {
    slug: "voice-settings",
    title: "Customizing Three Little Birds voice",
    category: "ai",
    summary: "Five presets + 500-char custom instructions, applied everywhere.",
    body: `# Customizing Three Little Birds voice

Go to **Settings**. Pick a preset:

- **Default** — neutral, balanced
- **Formal** — no contractions, precise, professional
- **Friendly** — warm, conversational, uses "you" and "we"
- **Concise** — short sentences, bulleted lists, no filler
- **Bold** — confident, declarative, no hedging

Add up to 500 characters of **custom instructions** — e.g. "End every response with one specific next step" or "Always reference our French Quarter location" or "Speak like a no-nonsense Brooklyn diner owner".

This voice applies to every plan section, regenerated content, mentor reply, focus list, grant draft, and service agent message — across every business in your account.`,
  },
];

// ---- Interactive tour ----
// Each step is a self-contained card with title, body, and optional "go to" link.
// We don't anchor to live DOM elements (brittle); instead we describe + offer navigation.

export const TOUR_STEPS = [
  {
    title: "1 · Dashboard",
    body: "Every business you create lives here. Each card shows a Launch-readiness grade (A-F), a Plan Health score, a Compliance Risk badge, and your three weekly focus actions.",
    icon: "LayoutDashboard",
    accent: "#4A4458",
    goto: "/dashboard",
  },
  {
    title: "2 · The 5-step Wizard",
    body: "Click 'New plan' on the Dashboard and answer 7 questions in 10-15 minutes. Behind the scenes, two parallel Three Little Birds jobs draft your plan and your roadmap.",
    icon: "Sparkles",
    accent: "#7C5FB0",
    goto: "/dashboard",
  },
  {
    title: "3 · Business Plan",
    body: "8 sections from refined description through SWOT and Plan Health. Each section is independently regenerable — iterate without losing your edits elsewhere.",
    icon: "FileText",
    accent: "#4A4458",
  },
  {
    title: "4 · Kanban Roadmap",
    body: "12-16 phased tasks across To Do → In Progress → Done. Drag to schedule, comment in real-time with collaborators, or open an in-app Service Agent (Stripe Atlas, ZenBusiness, Mercury…) for any task.",
    icon: "Trello",
    accent: "#7C5FB0",
  },
  {
    title: "5 · Compliance, Grants, Goals",
    body: "Federal/state/city/industry compliance items tracked with severity. Live grants from Grants.gov. SMART goals with progress tracking. All Three Little Birds-tailored to your specific business.",
    icon: "ShieldCheck",
    accent: "#4A4458",
    goto: "/compliance",
  },
  {
    title: "6 · CRM & Documents",
    body: "Standard+ unlocks a native CRM on every plan — track contacts, companies and a drag-and-drop deal pipeline whose closed-won deals feed your Goals and Runway. The Document Library stores contracts, invoices and licenses with folders, tags and research of images + PDFs. See it all in the workspace CRM Dashboard (/crm) and Document Library (/documents).",
    icon: "Briefcase",
    accent: "#0D9488",
    goto: "/crm",
  },
  {
    title: "7 · Your Founder Mentor",
    body: "The orange button bottom-right is your Founder Mentor — it can see every business you've created. Ask it anything, in your voice. That's it. You're ready.",
    icon: "Sparkles",
    accent: "#7C5FB0",
  },
];

// ---- Pre-login intro story ----
// 6-scene narrative — auto-advances every ~6.5s; user can skip / replay.

export const INTRO_SCENES = [
  {
    eyebrow: "The truth nobody tells you",
    headline: "Starting a business is\nterrifying.",
    body: "Most ideas die in a notes app. Not because they're bad — because the path forward is invisible.",
    tone: "dark",
    accent: "#7C5FB0",
  },
  {
    eyebrow: "The fear has a shape",
    headline: "What if I run\nout of money?",
    body: "What if it's illegal? What if nobody buys it? What if I miss something I didn't know I needed to know?",
    tone: "dark",
    accent: "#7C5FB0",
    stats: [
      { n: "82%", label: "Cite cash flow as their #1 fear" },
      { n: "67%", label: "Don't know which licenses they need" },
      { n: "44%", label: "Feel paralyzed, not motivated" },
    ],
  },
  {
    eyebrow: "Meet Three Little Birds",
    headline: "Fear shrinks when\nthe path is visible.",
    body: "An operating system that turns one idea into a strategist-grade plan, a 12-task roadmap, a real budget, every compliance filing, and the three things to do this week.",
    tone: "light",
    accent: "#4A4458",
  },
  {
    eyebrow: "What you get",
    headline: "Everything a co-founder\nwould build with you.",
    body: "Three Little Birds-generated business plan · Kanban execution roadmap · Live compliance tracker · Live grants finder · Native CRM & sales pipeline · Document Library · Plan health check · Weekly focus · Founder Mentor",
    tone: "dark",
    accent: "#4A4458",
    chips: ["Plan", "Roadmap", "Budget", "Compliance", "Grants", "CRM", "Documents", "Goals", "Focus", "Mentor"],
  },
  {
    eyebrow: "The math of clarity",
    headline: "From idea to\nlaunch-ready in 30 days.",
    body: "Most founders spend 6 months stuck on what to do next. With Three Little Birds, the next move is always one click away.",
    tone: "light",
    accent: "#7C5FB0",
  },
  {
    eyebrow: "Three little birds",
    headline: "Don't worry\nabout a thing.",
    body: "Sign in with Google. Spin up your first plan in 15 minutes. Every little thing is gonna be alright.",
    tone: "dark",
    accent: "#7C5FB0",
    cta: true,
  },
];
